import React, { useEffect, useState } from "react";
import { useServiceProductsQuery } from "../../Store/Apis/servicesApi";
import {
  useCreateCustomerMutation,
  useCustomersQuery,
} from "../../Store/Apis/customerApi";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { DatePicker } from "antd";
import moment from "moment";
import { allCountries } from "country-telephone-data";
import Error from "../../Components/Error/Error";
import Loader from "../../Components/Loader/Loader";
import { useSelector } from "react-redux";
import AddPaymentForm from "../../Components/AddPaymentForm/AddPaymentForm";
import { useTaxPercentageQuery } from "../../Store/Apis/toolsApi";
import {
  useOrderByCustomerIdQuery,
  useOrderByIdQuery,
} from "../../Store/Apis/bookingApi";
import {
  fetchRoomTypes,
  checkRoomAvailability,
  getCompany,
  checkVenueAvailability,
  resendBookingEmail,
} from "../../Store/Apis/ReservationApi";
import { CiWarning } from "react-icons/ci";
import { IoIosPeople } from "react-icons/io";
const ACCOMMODATION_TYPE_ID = 0; // TODO: replace with real ID when available from API
// Flexible accommodation detector with explicit flag support.
const isAccommodation = (s) => {
  if (!s) return false;

  if (s.accommodation) return true; // explicit flag when stored

  // Check by type ID if available

  if (
    ACCOMMODATION_TYPE_ID &&
    Number(s.typeId) === Number(ACCOMMODATION_TYPE_ID)
  )
    return true;

  // Check by parent ID - accommodation services
  if (Number(s.parentId) === 212) return true;

  // Get the service name from various possible properties

  const n = (
    s.name ||
    s.typeName ||
    s.productServiceName ||
    s.rawName ||
    s.serviceName ||
    ""
  ).toLowerCase();

  if (!n) return false;

  // Direct accommodation/room keywords

  if (n.includes("accom")) return true; // covers accommodation / accom.

  if (n.includes("room")) return true;

  // Common accommodation packages that may not contain "room"

  if (n.includes("bed only") || n.includes("bed-only") || n.includes("bedonly"))
    return true;

  if (
    n.includes("room only") ||
    n.includes("room-only") ||
    n.includes("roomonly")
  )
    return true;

  // Check for accommodation-specific patterns from your API data

  if (n.includes("single") || n.includes("double")) return true;

  if (n.includes("executive") || n.includes("suite")) return true;

  if (n.includes("b&b") || n.includes("bb") || n.includes("b-b")) return true;

  if (n.includes("dbb") || n.includes("d-bb")) return true; // dinner, bed & breakfast

  if (n.includes("b/o") || n.includes("bo") || n.includes("bed/breakfast"))
    return true;

  // Specific patterns from your API data

  if (n.includes("accommodation") || n.includes("accomodation")) return true;

  // Composite meal + bed patterns (traditional approach)

  const mealKeywords = ["bed", "breakfast", "dinner"];

  const kwHits = mealKeywords.filter((k) => n.includes(k)).length;

  if (kwHits >= 2) return true;

  // Additional check: if it's explicitly a "bed" service (common in accommodation)

  if (
    n.includes("bed") &&
    (n.includes("night") || n.includes("stay") || n.includes("guest"))
  ) {
    return true;
  }

  return false;
};

const AddEditBooking = ({
  onSubmit,
  data,
  paymentData,
  isSubmitting,
  onResendDocument,
}) => {
  // Helper placed inside component so it can access getNameKey defined later
  const getNameKey = (x) =>
    (
      x?.typeName ||
      x?.productServiceName ||
      x?.name ||
      x?.rawName ||
      x?.serviceName ||
      ""
    )
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const dedupeGuestServices = (arr = []) => {
    const map = new Map();
    arr.forEach((s) => {
      const key = `${getNameKey(s)}::${Number(s.childId)}`;
      map.set(key, s);
    });
    return Array.from(map.values());
  };
  const user = useSelector((state) => state.auth.user);

  // --- Added missing state declarations ---
  const [roomTypes, setRoomTypes] = useState([]);
  const [loadingRoomTypes, setLoadingRoomTypes] = useState(false);
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [roomsAvailable, setRoomsAvailable] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  // Venue availability states (mirrors room availability pattern)
  const [availableVenues, setAvailableVenues] = useState([]);
  const [venuesAvailable, setVenuesAvailable] = useState(null);
  const [checkingVenueAvailability, setCheckingVenueAvailability] =
    useState(false);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Track original item count to distinguish existing vs new items
  const [originalItemCount, setOriginalItemCount] = useState(0);
  // Snapshots of original services for diffing (edit mode)
  const [originalIndividualServices, setOriginalIndividualServices] = useState(
    [],
  ); // rooms + children flattened
  // Composite keys of original individual items to filter out on submit (avoid re-sending existing)
  const [originalIndividualKeyList, setOriginalIndividualKeyList] = useState(
    [],
  );
  const [originalCompanyServices, setOriginalCompanyServices] = useState([]); // flattened guest services
  // Snapshot of original company subCustomers (with their services) for diffing existing guest edits
  const [originalSubCustomers, setOriginalSubCustomers] = useState([]);

  // Edit mode optimization: track original venue/date details to avoid unnecessary availability checks
  const [originalVenueDetails, setOriginalVenueDetails] = useState({
    selectedDate: null,
    venueServices: [],
    isEdit: false,
    hydrationComplete: false,
  });

  // Customer type selection
  const [customerType, setCustomerType] = useState("individual");

  // Form fields for individual customer
  const [customerForm, setCustomerForm] = useState({
    salutation: "Mr",
    customerName: "",
    countryCode: "+27",
    phoneNumber: "",
    email: "",
    isCustomerSelected: false,
    customerID: null,
  });

  // Company booking state
  const [companyForm, setCompanyForm] = useState({
    companyName: "",
    companyID: null,
    companyAddress: "",
    companyContact: "",
    companyEmail: "", // added
    creditLimit: 0,
    currentLimit: 0,
    isCompanySelected: false,
  });

  // Sub-customers for company bookings
  const [subCustomers, setSubCustomers] = useState([]);
  // Active guest index for assigning services when company booking
  const [activeGuestIndex, setActiveGuestIndex] = useState(null);

  const [sortFilter, setSortFilter] = useState({
    productSearch: "",
    customerSearch: "",
    outletID: 4, // force outletID = 4
  });
  // Moved here so it's initialized before being used in dependencies
  const [filterType, setFilterType] = useState("All");

  // Service products query
  const serviceProductsApi = useServiceProductsQuery(
    {
      keyname: sortFilter.productSearch,
      outletID: sortFilter.outletID,
      // Pass companyID when company booking else pass customerID (backend handles either)
      ...(customerType === "company" && companyForm.companyID
        ? { companyID: companyForm.companyID }
        : {}),
      ...(customerType !== "company" && customerForm.customerID
        ? { customerID: customerForm.customerID }
        : {}),
    },
    { skip: !sortFilter.outletID },
  );

  // Populate filteredProducts from API (restored logic)
  useEffect(() => {
    if (serviceProductsApi.isSuccess) {
      let items =
        serviceProductsApi.data?.data || serviceProductsApi.data || [];
      if (!Array.isArray(items)) items = [];

      // Filter out inactive parent items (0 = inactive, 1 = active)
      items = items.filter((p) => p.isActive !== 0 && p.isActive !== false);

      // Filter out inactive child records and handle quantity-based disabling for products
      // Filter out inactive child records and handle quantity-based disabling for products
      items = items.map((p) => ({
        ...p,
        childRecords: (p.childRecords || [])
          .filter(
            (child) =>
              child.child_is_active !== 0 && child.child_is_active !== false,
          )
          .map((child) => ({
            ...child,
            // For products (type: 1), disable if quantity is 0
            isDisabled: p.type === 1 && Number(child.quantity || 0) === 0,
          })),
      }));

      if (filterType === "Product") items = items.filter((p) => p.type === 1);
      else if (filterType === "Service")
        items = items.filter((p) => p.type === 2);
      setFilteredProducts(items);
    } else if (serviceProductsApi.isError) {
      setFilteredProducts([]);
    }
  }, [
    serviceProductsApi.data,
    serviceProductsApi.isSuccess,
    serviceProductsApi.isError,
    filterType,
  ]);

  // Customer search query
  const customerApi = useCustomersQuery(
    {
      keyword: customerForm.customerName,
      outletID: sortFilter.outletID,
    },
    { skip: !sortFilter.outletID || !customerForm.customerName },
  );

  // Initialize createCustomer mutation
  const [createCustomer, createCustomerResponse] = useCreateCustomerMutation();

  const [selectedChildren, setSelectedChildren] = useState([]);
  // Product details modal state (reintroduced)
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // Staged selections inside the Product Details modal: { [childId]: quantity }
  const [modalSelections, setModalSelections] = useState({});
  // Bill-to-Account toggle
  const [isOnAccount, setIsOnAccount] = useState(false);
  // Quotation toggle (when billing to account)
  const [createQuotation, setCreateQuotation] = useState(false);

  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  // Internal render bump to ensure recalculation when switching guest cards
  const [guestActivationTick, setGuestActivationTick] = useState(0);

  // Number of guests state (only shows for accommodation services)
  const [numberOfGuests, setNumberOfGuests] = useState(1);

  // Helper function to determine if any selected services are accommodation-type
  const hasAccommodationServices = () => {
    if (customerType === "company") {
      return subCustomers.some((guest) =>
        (guest.services || []).some((service) => isAccommodation(service)),
      );
    } else {
      // For individual bookings, check both selectedRooms and selectedChildren
      const allSelected = [...selectedRooms, ...selectedChildren];
      return allSelected.some((service) => isAccommodation(service));
    }
  };

  // Helper function to determine if any selected services are venue-type (non-accommodation)
  const hasVenueServices = () => {
    if (customerType === "company") {
      // Check guest services if guests exist
      const guestVenueServices = subCustomers.some((guest) =>
        (guest.services || []).some((service) => !isAccommodation(service)),
      );

      // Also check selectedRooms/selectedChildren for company venue bookings (no guests)
      const globalVenueServices = [...selectedRooms, ...selectedChildren].some(
        (service) => !isAccommodation(service),
      );

      return guestVenueServices || globalVenueServices;
    } else {
      // For individual bookings, check both selectedRooms and selectedChildren
      const allSelected = [...selectedRooms, ...selectedChildren];
      return allSelected.some((service) => !isAccommodation(service));
    }
  };

  // Helper function to determine if any selected services are from parentId 219 (for date fields)
  const hasParentId219Services = () => {
    if (customerType === "company") {
      return subCustomers.some((guest) =>
        (guest.services || []).some((service) =>
          [212].includes(Number(service.parentId)),
        ),
      );
    } else {
      // For individual bookings, check both selectedRooms and selectedChildren
      const allSelected = [...selectedRooms, ...selectedChildren];
      return allSelected.some((service) =>
        [212].includes(Number(service.parentId)),
      );
    }
  };

  // Helper function to determine if any selected services are from parentId 218/251 (for guest count)
  const hasParentId218Services = () => {
    if (customerType === "company") {
      return subCustomers.some((guest) =>
        (guest.services || []).some(
          (service) => Number(service.parentId) === 251,
        ),
      );
    } else {
      // For individual bookings, check both selectedRooms and selectedChildren
      const allSelected = [...selectedRooms, ...selectedChildren];
      return allSelected.some((service) => Number(service.parentId) === 251);
    }
  };

  // Helper function to check if a service is a venue (parentId 251)
  const isVenueService = (service) => {
    return Number(service.parentId) === 251;
  };

  // Helper function to check if any selected services are venue services (parentId 251)
  const hasVenueServicesSpecific = () => {
    if (customerType === "company") {
      return subCustomers.some((guest) =>
        (guest.services || []).some((service) => isVenueService(service)),
      );
    } else {
      const allSelected = [...selectedRooms, ...selectedChildren];
      return allSelected.some((service) => isVenueService(service));
    }
  };

  // 🔒 COOL HELPER: Check if this is a confirmed venue booking (individual or company)
  const isConfirmedVenueBooking = () => {
    // Must be in edit mode with actual data
    if (!data || (!data.orderID && !data.orderId)) {
      return false;
    }

    // Must be either individual venue booking OR company venue booking (no guests)
    if (customerType === "company" && subCustomers.length > 0) {
      return false; // Company with guests is accommodation, not venue
    }

    // Must have confirmed status (statusId 8)
    const statusId = Number(data?.statusID || data?.statusId || 0);
    if (statusId !== 8) {
      return false;
    }

    // Must have venue services selected
    const hasVenueServicesSelected = hasVenueServices();

    return hasVenueServicesSelected;
  };

  // Reset number of guests when both accommodation and venue services are deselected
  useEffect(() => {
    if (!hasParentId218Services() && !hasVenueServices()) {
      setNumberOfGuests(1);
    }
  }, [customerType, selectedRooms, selectedChildren, subCustomers]);

  // Determine what type of date fields to show
  const getDateFieldType = () => {
    // For company bookings, always use single date for venues or dual dates for accommodation
    if (customerType === "company") {
      if (hasAccommodationServices()) {
        return "dual"; // Show check-in and check-out dates for accommodation
      }
      return "single"; // Show single date for venues
    }

    // For individual bookings (existing logic)
    const hasServices = selectedRooms.length > 0 || selectedChildren.length > 0;

    if (!hasServices) {
      return "single"; // Default to single date when no services selected
    }

    // Check if accommodation services (parentId 219) are selected
    if (hasParentId219Services() || hasAccommodationServices()) {
      return "dual"; // Show check-in and check-out dates
    }

    return "single"; // Show single date for venues and other services
  };

  // Helper function to determine if venue availability check is needed (optimization for edit mode)
  const shouldCheckVenueAvailability = () => {
    // Always check for new bookings (no original data)
    if (!data) {
      return true;
    }

    // Edit mode: skip venue availability if the booking has no venue/accommodation parent services (251 or 212)
    const allSelected = [...selectedRooms, ...selectedChildren];
    const hasVenueParentIds = allSelected.some((s) =>
      [251, 212].includes(Number(s.parentId)),
    );
    if (!hasVenueParentIds) {
      console.log(
        "Edit mode: No services with parentId 251 or 212, skipping venue availability check",
      );
      return false;
    }

    // For edit mode: if hydration isn't complete, only check if date has explicitly changed
    if (
      !originalVenueDetails.isEdit ||
      !originalVenueDetails.hydrationComplete
    ) {
      // If we have a previous selectedDate captured and it differs from current, allow check
      if (
        originalVenueDetails.selectedDate &&
        selectedDate !== originalVenueDetails.selectedDate
      ) {
        console.log(
          "Edit mode: Date changed during hydration, venue availability check required",
        );
        return true;
      }

      console.log(
        "Edit mode hydration not complete, skipping availability check",
      );
      return false; // Don't check until hydration is complete
    }

    // Check if date has changed
    if (selectedDate !== originalVenueDetails.selectedDate) {
      return true;
    }

    // Check if venue services have changed
    const currentVenueServices = [...selectedRooms, ...selectedChildren].filter(
      (service) => !isAccommodation(service),
    );

    // Simple comparison: if count differs, definitely changed
    if (
      currentVenueServices.length !== originalVenueDetails.venueServices.length
    ) {
      return true;
    }

    // Compare service IDs/types
    const currentServiceIds = currentVenueServices
      .map((s) => ({
        childId: s.childId,
        parentId: s.parentId,
        typeId: s.typeId,
      }))
      .sort((a, b) => Number(a.childId) - Number(b.childId));

    const originalServiceIds = originalVenueDetails.venueServices
      .map((s) => ({
        childId: s.childId,
        parentId: s.parentId,
        typeId: s.typeId,
      }))
      .sort((a, b) => Number(a.childId) - Number(b.childId));

    // Compare arrays
    const servicesChanged =
      JSON.stringify(currentServiceIds) !== JSON.stringify(originalServiceIds);

    if (servicesChanged) {
      return true;
    }

    // Special case: If statusID = 7 (pending), always check venue availability
    // even if nothing has changed, because pending bookings need validation
    const currentStatusID = Number(data?.statusID || 0);
    if (currentStatusID === 7) {
      return true;
    }
    return false;
  };

  // Modals
  const [paymentFormModal, setPaymentFormModal] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [quotationEmail, setQuotationEmail] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [docSendType, setDocSendType] = useState("proforma");
  // Credit limit confirmation modal
  const [limitModal, setLimitModal] = useState({
    show: false,
    reason: "",
    diff: 0,
    available: 0,
  });

  // Account on hold confirmation modal
  const [accountHoldModal, setAccountHoldModal] = useState({
    show: false,
    customerType: "",
    customerName: "",
  });

  // Build initial staged selections for the modal based on current selections

  const buildInitialModalSelections = (product) => {
    const next = {};
    const children = product?.childRecords || [];
    const idsOf = (obj) =>
      [
        obj?.childId,
        obj?.serviceId,
        obj?.serviceID,
        obj?.serviceTypeId,
        obj?.typeId,
      ]
        .filter((v) => v !== undefined && v !== null)
        .map((v) => v.toString());
    const childMatches = (child, item) => {
      const cids = idsOf(child);
      const iids = idsOf(item || {});
      return cids.some((id) => iids.includes(id));
    };
    if (customerType === "company") {
      // For company bookings with guest cards - check active guest's services
      if (activeGuestIndex !== null && subCustomers[activeGuestIndex]) {
        const guest = subCustomers[activeGuestIndex] || {};
        const current = guest.services || [];
        children.forEach((child) => {
          // Skip disabled children (out of stock for products)
          if (child.isDisabled) return;

          const found = current.find((s) => childMatches(child, s));
          if (found) next[child.childId] = found.quantity || 1;
        });
      }

      // For company venue bookings (no guest cards) OR when no active guest - also check selectedRooms/selectedChildren
      if (activeGuestIndex === null || subCustomers.length === 0) {
        children.forEach((child) => {
          // Skip disabled children (out of stock for products)
          if (child.isDisabled) return;

          const fromChildren = selectedChildren.find((c) =>
            childMatches(child, c),
          );
          const fromRooms = selectedRooms.find((r) => childMatches(child, r));
          const picked = fromChildren || fromRooms;
          if (picked) next[child.childId] = picked.quantity || 1;
        });
      }
    } else {
      children.forEach((child) => {
        // Skip disabled children (out of stock for products)
        if (child.isDisabled) return;

        // Skip disabled children (out of stock for products)
        if (child.isDisabled) return;

        const fromChildren = selectedChildren.find((c) =>
          childMatches(child, c),
        );
        const fromRooms = selectedRooms.find((r) => childMatches(child, r));
        const picked = fromChildren || fromRooms;
        if (picked) next[child.childId] = picked.quantity || 1;
      });
    }
    return next;
  };

  // Open Product modal (staged selection; commit on Confirm)
  const handleSelectProduct = (product) => {
    // Only require guest card selection if guest cards exist
    if (
      customerType === "company" &&
      subCustomers.length > 0 &&
      activeGuestIndex === null
    ) {
      toast.error("Select a guest card first");
      return;
    }
    setSelectedProduct(product);
    setModalSelections(buildInitialModalSelections(product));
    setIsProductModalOpen(true);
  };

  // Rebuild staged modal selections if active guest changes while modal is open
  useEffect(() => {
    if (!isProductModalOpen) return;
    if (customerType !== "company") return; // only relevant for company
    if (activeGuestIndex === null) return;
    if (!selectedProduct) return;
    // Rebuild from the newly active guest's current services
    setModalSelections(buildInitialModalSelections(selectedProduct));
  }, [activeGuestIndex]);

  // Child selection toggle (individual vs company guest assignment)
  const handleSelectChild = (child) => {
    if (customerType === "company") {
      // If guest cards exist, require selection and assign to guest
      if (subCustomers.length > 0) {
        if (activeGuestIndex === null) {
          toast.error("Select a guest card first");
          return;
        }

        setSubCustomers((prev) => {
          const copy = [...prev];
          const guest = copy[activeGuestIndex];
          const exists = guest.services.some(
            (s) => Number(s.childId) === Number(child.childId),
          );
          if (exists) {
            guest.services = guest.services.filter(
              (s) => Number(s.childId) !== Number(child.childId),
            );
          } else {
            const rawName = child.typeName || child.productServiceName || "";
            const accommodationFlag = isAccommodation(child);
            guest.services.push({
              childId: child.childId,
              rate: child.rate,
              quantity: 1,
              typeId: child.typeId,
              serviceTypeId: child.typeId, // Add for proper ID mapping
              serviceId: child.serviceId || child.childId, // Add for edit mode
              name: rawName,
              rawName,
              accommodation: accommodationFlag,
              parentId: child.parentId || 0,
            });
          }
          copy[activeGuestIndex] = { ...guest };
          return copy;
        });
      } else {
        // No guest cards - only allow venue services, block accommodation
        if (isAccommodation(child)) {
          toast.error(
            "Accommodation services require individual guests. Please add a guest first.",
          );
          return;
        }

        // Treat like individual booking (venue mode)
        const cid = Number(child.childId);
        const tid = Number(child.typeId);
        const pid = Number(child.parentId);

        if (isAccommodation(child)) {
          setSelectedRooms((prev) => {
            const exists = prev.some((it) => Number(it.childId) === cid);
            if (exists) return prev.filter((it) => Number(it.childId) !== cid);
            setSelectedChildren((pc) =>
              pc.filter((c) => Number(c.childId) !== cid),
            );
            return [
              ...prev,
              {
                childId: cid,
                typeId: tid,
                serviceTypeId: tid, // Add for proper ID mapping
                serviceId: child.serviceId || cid, // Add for edit mode
                parentId: pid,
                typeName: child.typeName,
                rate: Number(child.rate),
                quantity: 1,
                maxQuantity: child.quantity,
              },
            ];
          });
        } else {
          setSelectedChildren((prev) => {
            const exists = prev.some((item) => Number(item.childId) === cid);
            if (exists)
              return prev.filter((item) => Number(item.childId) !== cid);
            setSelectedRooms((pr) =>
              pr.filter((r) => Number(r.childId) !== cid),
            );
            return [
              ...prev,
              {
                childId: cid,
                typeId: tid,
                serviceTypeId: tid, // Add for proper ID mapping
                serviceId: child.serviceId || cid, // Add for edit mode
                parentId: pid,
                typeName: child.typeName,
                rate: Number(child.rate),
                quantity: 1,
                maxQuantity: child.quantity,
              },
            ];
          });
        }
      }
    } else {
      const cid = Number(child.childId);
      const tid = Number(child.typeId);
      const pid = Number(child.parentId);
      // Route accommodations to selectedRooms to avoid cross-list duplicates
      if (isAccommodation(child)) {
        setSelectedRooms((prev) => {
          const exists = prev.some((it) => Number(it.childId) === cid);
          if (exists) return prev.filter((it) => Number(it.childId) !== cid);
          // Also remove from children if present
          setSelectedChildren((pc) =>
            pc.filter((c) => Number(c.childId) !== cid),
          );
          return [
            ...prev,
            {
              childId: cid,
              typeId: tid,
              serviceTypeId: tid, // Add for proper ID mapping
              serviceId: child.serviceId || cid, // Add for edit mode
              parentId: pid,
              typeName: child.typeName,
              rate: Number(child.rate),
              quantity: 1,
              maxQuantity: child.quantity,
            },
          ];
        });
      } else {
        setSelectedChildren((prev) => {
          const exists = prev.some((item) => Number(item.childId) === cid);
          if (exists)
            return prev.filter((item) => Number(item.childId) !== cid);
          // Also remove from rooms if present
          setSelectedRooms((pr) => pr.filter((r) => Number(r.childId) !== cid));
          return [
            ...prev,
            {
              ...child,
              serviceTypeId: child.typeId, // Add for proper ID mapping
              serviceId: child.serviceId || child.childId, // Add for edit mode
              quantity: 1,
              rate: Number(child.rate),
              maxQuantity: child.quantity,
            },
          ];
        });
      }
    }
  };

  // Quantity change for selected child (from modal or summary table)
  const handleQuantityChange = (child, type, guestIndexOverride) => {
    if (customerType === "company") {
      // Prefer an explicit guestIndex (e.g. from a summary-table row) so this
      // always targets the guest the row actually belongs to; fall back to
      // activeGuestIndex only when no explicit guest is known (e.g. the
      // add-product modal, which operates on the currently selected card).
      const targetGuestIndex =
        guestIndexOverride !== undefined && guestIndexOverride !== null
          ? guestIndexOverride
          : activeGuestIndex;
      // FIXED: Check if we have guest cards or not
      if (subCustomers.length > 0 && targetGuestIndex !== null) {
        // Company with guest cards - update the target guest's services
        setSubCustomers((prev) => {
          const copy = [...prev];
          const guest = copy[targetGuestIndex];
          guest.services = guest.services.map((s) => {
            if (s.childId !== child.childId) return s;
            let q = s.quantity || 1;
            if (type === "increment") q++;
            else if (type === "decrement") q--;
            if (q < 1) q = 1;
            return { ...s, quantity: q };
          });
          copy[targetGuestIndex] = { ...guest };
          return copy;
        });
      } else {
        // Company venue booking (no guest cards) - update selectedRooms/selectedChildren
        setSelectedRooms((prev) =>
          prev.map((item) => {
            if (item.childId !== child.childId) return item;
            let q = item.quantity || 1;
            if (type === "increment") q = q + 1;
            else if (type === "decrement") q = q - 1;
            if (q < 1) q = 1;
            return { ...item, quantity: q };
          }),
        );
        setSelectedChildren((prev) =>
          prev.map((item) => {
            if (item.childId !== child.childId) return item;
            let q = item.quantity || 1;
            if (type === "increment") q = q + 1;
            else if (type === "decrement") q = q - 1;
            if (q < 1) q = 1;
            return { ...item, quantity: q };
          }),
        );
      }
    } else {
      // Individual booking - Handle both selectedRooms and selectedChildren
      setSelectedRooms((prev) =>
        prev.map((item) => {
          if (item.childId !== child.childId) return item;
          let q = item.quantity || 1;
          if (type === "increment") q = q + 1;
          else if (type === "decrement") q = q - 1;
          if (q < 1) q = 1;
          return { ...item, quantity: q };
        }),
      );
      setSelectedChildren((prev) =>
        prev.map((item) => {
          if (item.childId !== child.childId) return item;
          let q = item.quantity || 1;
          if (type === "increment") q = q + 1;
          else if (type === "decrement") q = q - 1;
          if (q < 1) q = 1;
          return { ...item, quantity: q };
        }),
      );
    }
  };

  // Remove ability to change rate once selected (function retained if needed elsewhere)
  const handleRateChange = () => {};

  // --- Per-guest services helpers (added) ---
  function calculateNights(checkIn, checkOut) {
    if (!checkIn || !checkOut) return 0;
    const start = moment(checkIn);
    const end = moment(checkOut);
    const diff = end.diff(start, "days");
    // treat same-day check-in/out as 1 night, never less than 1 if both provided
    return diff > 0 ? diff : 1;
  }

  const guestTotal = React.useCallback(
    (guest) => {
      if (!guest || !guest.services) return 0;

      // Calculate nights from guest-specific dates first
      let nights = calculateNights(
        guest.customerCheckIn,
        guest.customerCheckOut,
      );

      // Fallback to overall booking dates if guest dates are missing/invalid
      if (!nights || nights === 0) {
        nights = calculateNights(checkInDate, checkOutDate);
      }

      // Final fallback: use 1 night minimum for accommodation services
      if (!nights || nights === 0) {
        nights = 1;
      }

      return guest.services.reduce((sum, s) => {
        if (isAccommodation(s)) {
          return sum + (s.rate || 0) * (s.quantity || 1) * nights;
        }
        return sum + (s.rate || 0) * (s.quantity || 1);
      }, 0);
    },
    [checkInDate, checkOutDate],
  );

  // Derive roomID for a company guest from its accommodation service or stored field
  const deriveRoomId = (guest) => {
    if (!guest) return 0;
    if (guest.roomID || guest.roomId)
      return Number(guest.roomID || guest.roomId || 0);
    const services = guest.services || [];
    // Prefer accommodation service room id
    const acc = services.find(
      (s) => isAccommodation(s) && (s.roomID || s.roomId),
    );
    if (acc) return Number(acc.roomID || acc.roomId || 0);
    // Fallback: any service that has a room id
    const anyWith = services.find((s) => s.roomID || s.roomId);
    if (anyWith) return Number(anyWith.roomID || anyWith.roomId || 0);
    return 0;
  };

  // Back-fill roomID onto subCustomers after hydration if available in services
  useEffect(() => {
    if (customerType !== "company" || subCustomers.length === 0) return;
    // Only update if at least one guest lacks roomID but has an accommodation service with one
    let needsUpdate = false;
    const next = subCustomers.map((g) => {
      if (g.roomID || g.roomId) return g;
      const acc = (g.services || []).find(
        (s) => isAccommodation(s) && (s.roomID || s.roomId),
      );
      if (acc) {
        needsUpdate = true;
        return { ...g, roomID: acc.roomID || acc.roomId };
      }
      return g;
    });
    if (needsUpdate) setSubCustomers(next);
  }, [customerType, subCustomers]);

  const handleServiceSelectForGuest = (guestIndex, service) => {
    setSubCustomers((prev) => {
      const copy = [...prev];
      const guest = copy[guestIndex];
      if (!guest.services) guest.services = [];
      const exists = guest.services.some((s) => s.childId === service.childId);
      if (exists) {
        guest.services = guest.services.filter(
          (s) => s.childId !== service.childId,
        );
      } else {
        const rawName = service.productServiceName || service.typeName || "";
        const accommodationFlag = isAccommodation(service);
        guest.services.push({
          childId: service.childId,
          rate: service.rate,
          quantity: 1,
          typeId: service.typeId,
          name: rawName,
          rawName,
          accommodation: accommodationFlag,
          parentId: service.parentId || 0,
        });
      }
      copy[guestIndex] = { ...guest };
      return copy;
    });
  };

  const updateGuestServiceQuantity = (guestIndex, childId, newQty) => {
    if (newQty <= 0) newQty = 1;
    setSubCustomers((prev) => {
      const copy = [...prev];
      const guest = copy[guestIndex];
      guest.services = guest.services.map((s) =>
        s.childId === childId ? { ...s, quantity: newQty } : s,
      );
      copy[guestIndex] = { ...guest };
      return copy;
    });
  };

  // Recompute subtotal including both rooms and selected children or per-guest totals.
  // For individual bookings, multiply accommodation lines by number of nights.
  const subtotal = React.useMemo(() => {
    if (customerType === "company") {
      // If there are guest cards, use guest totals
      if (subCustomers.length > 0) {
        return subCustomers.reduce((acc, sub) => acc + guestTotal(sub), 0);
      }
      // No guest cards - venue booking mode - calculate from selectedRooms/selectedChildren
      const n = calculateNights(checkInDate, checkOutDate);
      const nightsVal = n > 0 ? n : 1;
      return [...selectedRooms, ...selectedChildren].reduce((acc, item) => {
        const base = (item.rate || 0) * (item.quantity || 1);
        return acc + (isAccommodation(item) ? base * nightsVal : base);
      }, 0);
    }

    const n = calculateNights(checkInDate, checkOutDate);
    const nightsVal = n > 0 ? n : 1; // default 1 until dates chosen

    const total = [...selectedRooms, ...selectedChildren].reduce(
      (acc, item) => {
        const base = (item.rate || 0) * (item.quantity || 1);
        return acc + (isAccommodation(item) ? base * nightsVal : base);
      },
      0,
    );

    return total;
  }, [
    customerType,
    subCustomers,
    selectedRooms,
    selectedChildren,
    checkInDate,
    checkOutDate,
    guestActivationTick,
    guestTotal,
  ]);

  useEffect(() => {
    if (user.outletID) loadRoomTypes();
  }, [user.outletID]);

  const loadRoomTypes = async () => {
    setLoadingRoomTypes(true);
    try {
      const rooms = await fetchRoomTypes(user.outletID);
      setRoomTypes(rooms || []);
    } catch (e) {
      toast.error("Failed to load room types");
    } finally {
      setLoadingRoomTypes(false);
    }
  };

  useEffect(() => {
    const dateFieldType = getDateFieldType();

    // Check room availability for dual date scenarios (accommodation)
    // Only check if we have accommodation services selected
    if (
      dateFieldType === "dual" &&
      checkInDate &&
      checkOutDate &&
      checkInDate < checkOutDate &&
      hasAccommodationServices()
    ) {
      checkAvailability();
    }

    // Check venue availability for single date scenarios (venues/events)
    // Only check if we have non-accommodation services selected (venues) AND optimization allows it
    if (
      dateFieldType === "single" &&
      selectedDate &&
      (selectedChildren.length > 0 || selectedRooms.length > 0) &&
      !hasAccommodationServices() &&
      !isConfirmedVenueBooking() && // 🔒 Skip for confirmed venue bookings
      shouldCheckVenueAvailability() // Added optimization check
    ) {
      checkVenueAvailabilityWrapper();
    }
  }, [
    checkInDate,
    checkOutDate,
    selectedDate,
    // selectedChildren,
    // selectedRooms,
  ]);

  const checkAvailability = async () => {
    setCheckingAvailability(true);
    try {
      const availability = await checkRoomAvailability(
        checkInDate,
        checkOutDate,
        4,
      ); // force outletID=4
      setAvailableRooms(availability || []);
      setRoomsAvailable(Array.isArray(availability) && availability.length > 0);
    } catch (e) {
      setRoomsAvailable(false);
      toast.error("Failed to check room availability");
    } finally {
      setCheckingAvailability(false);
    }
  };

  // New function to check venue availability for single date scenarios
  const checkVenueAvailabilityWrapper = async () => {
    setCheckingVenueAvailability(true);
    try {
      // Get the first selected venue service to extract parentId and typeId
      const allSelectedServices = [...selectedRooms, ...selectedChildren];
      const venueService = allSelectedServices.find(
        (service) => !isAccommodation(service),
      );

      if (!venueService) {
        console.warn("No venue service found for availability checking");
        setVenuesAvailable(false);
        setCheckingVenueAvailability(false);
        return;
      }

      console.log("Venue service for availability check:", venueService);

      const venueTypeID = Number(
        venueService.typeId || venueService.childId || 0,
      );

      console.log("Checking venue availability with:", {
        selectedDate,
        venueTypeID,
        serviceName: venueService.typeName || venueService.productServiceName,
      });

      const availability = await checkVenueAvailability(
        selectedDate,
        null,
        venueTypeID, // typeId from selected venue service
        4, // force outletID=4
      );
      setAvailableVenues(availability?.venues || []);

      // Check if venues are available based on API response structure
      const venuesAreAvailable = (() => {
        if (!availability) return false;

        // Check if we have venues in response
        const venues = availability.venues || [];
        if (venues.length === 0) return false;

        // Check if any venue is both not booked and available
        return venues.some(
          (venue) => venue.isAvailable === true && venue.isBooked === false,
        );
      })();

      setVenuesAvailable(venuesAreAvailable);

      console.log("Venue availability check result:", {
        availability,
        venuesAreAvailable,
        venuesInResponse: availability?.venues?.length || 0,
      });
    } catch (e) {
      setVenuesAvailable(false);
      toast.error("Failed to check venue availability");
      console.error("Venue availability check error:", e);
    } finally {
      setCheckingVenueAvailability(false);
    }
  };

  const handleCustomerTypeChange = (type) => {
    setCustomerType(type);
    setCustomerForm({
      salutation: "Mr",
      customerName: "",
      countryCode: "+27",
      phoneNumber: "",
      email: "",
      isCustomerSelected: false,
      customerID: null,
    });
    setCompanyForm({
      companyName: "",
      companyID: null,
      companyAddress: "",
      companyContact: "",
      companyEmail: "",
      creditLimit: 0,
      currentLimit: 0,
      isCompanySelected: false,
    });
    setSubCustomers([]);
    setShowSuggestions(false);
    setIsOnAccount(false);
    setCreateQuotation(false);
    setNumberOfGuests(1); // Reset number of guests
  };

  const handleCustomerFormChange = (e) => {
    const { name, value } = e.target;
    setCustomerForm((prev) => ({
      ...prev,
      [name]: value,
      isCustomerSelected: false,
    }));
    if (name === "customerName") setShowSuggestions(value.trim().length > 0);
  };

  const handleCompanyFormChange = async (e) => {
    const { name, value } = e.target;
    setCompanyForm((prev) => ({
      ...prev,
      [name]: value,
      isCompanySelected: false,
    }));
    if (name === "companyName" && value.trim().length > 1) {
      try {
        const companies = await getCompany({
          companyName: value,
          outletID: user.outletID,
        }); // correct param casing
        setCompanySuggestions(companies || []);
      } catch (err) {
        console.error("Error fetching companies", err);
      }
    } else if (name === "companyName" && value.trim().length === 0) {
      setCompanySuggestions([]);
    }
  };

  const handleCustomerSelect = (customer) => {
    // Debug: Log the customer data to see what creditAccStatus we're getting
    console.log("Selected customer data:", customer);
    console.log("creditAccStatus:", customer?.creditAccStatus);

    setSelectedCustomer(customer); // Store the full customer object with all fields including creditAccStatus
    setCustomerForm((prev) => ({
      ...prev,
      customerName: customer.customerName,
      customerID: customer.customerID,
      phoneNumber: customer.phoneNumber || customer.phone || "",
      email:
        customer.email || customer.customerEmail || customer.emailAddress || "",
      salutation: customer.salutation || "Mr",
      countryCode: customer.countryCode || "+27",
      isCustomerSelected: true,
    }));
    setShowSuggestions(false);
  };

  const handleCompanySelect = (company) => {
    setCompanyForm((prev) => ({
      ...prev,
      companyName: company.companyName,
      companyID: company.companyID,
      companyAddress:
        company.companyAddress ||
        company.companyAddress ||
        company.company_Address ||
        company.address ||
        "",
      companyContact:
        company.companyPhoneNumber ||
        company.companyContact ||
        company.phoneNumber ||
        "",
      companyEmail:
        company.companyEmailAddress ||
        company.companyEmail ||
        company.email ||
        "",
      creditLimit:
        company.limit ??
        company.creditLimit ??
        company.credit_Limit ??
        company.credit_limit ??
        0,
      currentLimit:
        company.currentLimit ??
        company.current_Limit ??
        company.availableLimit ??
        0,
      creditAccStatus: company.creditAccStatus, // Preserve credit account status
      isCompanySelected: true,
    }));
    setCompanySuggestions([]);
  };

  const addSubCustomer = () => {
    setSubCustomers((prev) => [
      ...prev,
      {
        customerName: "",
        customerEmail: "",
        customerCheckIn: null,
        customerCheckOut: null,
        availability: null,
        services: [], // per-guest selected services
      },
    ]);
    setActiveGuestIndex((prev) =>
      prev === null
        ? 0
        : prev + 1 === subCustomers.length + 1
          ? prev + 1
          : prev,
    );

    // Clear venue dates when adding guests (switching from venue to accommodation booking)
    setSelectedDate(null);
    setCheckInDate(null);
    setCheckOutDate(null);
  };
  // Remove a sub customer by index
  const removeSubCustomer = (index) => {
    setSubCustomers((prev) => prev.filter((_, i) => i !== index));
  };
  const updateSubCustomer = (index, field, value) => {
    setSubCustomers((prev) => {
      const copy = [...prev];
      copy[index][field] = value;
      // Reset availability if date fields changed
      if (field === "customerCheckIn" || field === "customerCheckOut") {
        const sc = copy[index];
        if (
          sc.customerCheckIn &&
          sc.customerCheckOut &&
          sc.customerCheckIn < sc.customerCheckOut
        ) {
          // trigger availability check per guest
          checkGuestAvailability(
            index,
            sc.customerCheckIn,
            sc.customerCheckOut,
          );
        } else {
          copy[index].availability = null;
        }
      }
      return copy;
    });
  };

  const checkGuestAvailability = async (idx, start, end) => {
    setSubCustomers((prev) =>
      prev.map((g, i) => (i === idx ? { ...g, availability: "checking" } : g)),
    );
    try {
      const availability = await checkRoomAvailability(start, end, 3);
      const available = Array.isArray(availability) && availability.length > 0;
      setSubCustomers((prev) =>
        prev.map((g, i) =>
          i === idx
            ? { ...g, availability: available ? "available" : "unavailable" }
            : g,
        ),
      );
    } catch (e) {
      setSubCustomers((prev) =>
        prev.map((g, i) => (i === idx ? { ...g, availability: "error" } : g)),
      );
    }
  };

  // Submit order: build payload and pass to parent
  const submitOrder = async (paymentData, statusId) => {
    // Guard: prevent duplicate submissions
    if (isSubmitting) {
      console.log("Submit blocked — already processing");
      return;
    }
    try {
      // Create customer first if it's a new individual customer
      let finalCustomerID = customerForm.customerID;
      if (
        customerType === "individual" &&
        (!customerForm.customerID || customerForm.customerID === 0) &&
        customerForm.customerName &&
        customerForm.phoneNumber
      ) {
        try {
          const customerBody = {
            salutation: customerForm.salutation,
            name: customerForm.customerName,
            dateOfBirth: "", // Not captured in current form, but required by API
            email: customerForm.email,
            clientType: customerType,
            countryCode: customerForm.countryCode,
            customerRating: 0,
            phone: customerForm.phoneNumber,
            taxNumber: "",
            locality: "",
            pin: "",
            address: "",
            isActive: 1,
            OutletID: sortFilter.outletID,
          };

          const customerResponse = await createCustomer(customerBody).unwrap();

          if (customerResponse?.status === 200) {
            finalCustomerID = customerResponse.data.customerID;
            toast.success("New customer created successfully");
          } else {
            toast.error(
              customerResponse?.message || "Failed to create customer",
            );
            return; // Don't proceed with booking if customer creation fails
          }
        } catch (error) {
          console.error("Customer creation failed:", error);
          toast.error("Failed to create customer. Please try again.");
          return; // Don't proceed with booking if customer creation fails
        }
      }

      let overallCheckIn = checkInDate;
      let overallCheckOut = checkOutDate;

      // For individual bookings with non-accommodation services (single date), use selectedDate for both check-in and check-out
      if (customerType === "individual") {
        const dateFieldType = getDateFieldType();
        if (
          dateFieldType === "single" &&
          selectedDate &&
          (!checkInDate || !checkOutDate)
        ) {
          overallCheckIn = selectedDate;
          overallCheckOut = selectedDate;
        }
      }

      // For company venue bookings (no guest cards), use selectedDate for both check-in and check-out
      if (
        customerType === "company" &&
        subCustomers.length === 0 &&
        selectedDate
      ) {
        overallCheckIn = selectedDate;
        overallCheckOut = selectedDate;
      }

      if (customerType === "company" && subCustomers.length > 0) {
        const validDates = subCustomers.filter(
          (s) => s.customerCheckIn && s.customerCheckOut,
        );
        if (validDates.length > 0) {
          overallCheckIn = validDates.reduce(
            (min, s) =>
              !min || s.customerCheckIn < min ? s.customerCheckIn : min,
            null,
          );
          overallCheckOut = validDates.reduce(
            (max, s) =>
              !max || s.customerCheckOut > max ? s.customerCheckOut : max,
            null,
          );
        }
      }
      // Build a working snapshot (avoid async state change before payload)
      const workingSubCustomers =
        customerType === "company"
          ? subCustomers.map((g) => ({
              ...g,
              services: dedupeGuestServices(g.services),
            }))
          : subCustomers;
      const allServices = buildServiceDetails();
      const isQuotation =
        statusId === 11 || paymentData?.createQuotation === true;
      const billToAccount =
        !isQuotation && (paymentData?.billToAccount === true || isOnAccount);
      // Determine if we are editing an existing order (needed for correct orderID)
      const isEditing = Boolean(data && (data.orderID || data.orderId));

      const orderBody = {
        customerName:
          customerType === "individual"
            ? customerForm.customerName
            : companyForm.companyName,
        customerID: customerType === "individual" ? finalCustomerID || 0 : 0,
        categoryID: user.serviceCategoryId || 0,
        companyID: customerType === "company" ? companyForm.companyID || 0 : 0,
        companyAddress:
          customerType === "company" ? companyForm.companyAddress : "",
        subCustomers:
          customerType === "company"
            ? (() => {
                const isEditing = Boolean(
                  data && (data.orderID || data.orderId),
                );
                if (!isEditing) {
                  // Create mode: send all guests
                  // Filter out incomplete guests for submission - only include guests with minimum required data
                  return workingSubCustomers
                    .filter((sub) => {
                      // For accommodation services, require complete guest data
                      if (hasAccommodationServices()) {
                        return (
                          sub.customerName &&
                          sub.customerCheckIn &&
                          sub.customerCheckOut
                        );
                      }
                      // For venue services, just require guest name
                      return sub.customerName;
                    })
                    .map((sub) => {
                      return {
                        customerID: sub.customerID || 0, // ⭐ FIX: Include customerID for new
                        roomId: deriveRoomId(sub),
                        roomID: deriveRoomId(sub),
                        companyID: companyForm.companyID || 0,
                        customerName: sub.customerName,
                        orderID: 0,
                        customerEmail: sub.customerEmail || "", // ⭐ FIX: Ensure empty string instead of null/undefined
                        customerCheckIn: sub.customerCheckIn,
                        customerCheckOut: sub.customerCheckOut,
                        services: sub.services || [],
                        totalAmount: guestTotal(sub),
                      };
                    });
                }
                // Editing an existing reservation
                const originalLen = originalSubCustomers.length;
                const toKey = (s) => `${Number(s.childId)}|${Number(s.typeId)}`;
                const existingGuestEdited = workingSubCustomers.some(
                  (g, idx) => {
                    if (idx >= originalLen) return false; // new guest, ignore for edit detection
                    const orig = originalSubCustomers[idx];
                    if (!orig) return false;
                    if (
                      orig.customerName !== g.customerName ||
                      orig.customerEmail !== g.customerEmail ||
                      orig.customerCheckIn !== g.customerCheckIn ||
                      orig.customerCheckOut !== g.customerCheckOut
                    )
                      return true;
                    const oServices = orig.services || [];
                    const cServices = g.services || [];
                    if (oServices.length !== cServices.length) return true;
                    const oMap = new Map(oServices.map((s) => [toKey(s), s]));
                    for (const cs of cServices) {
                      const os = oMap.get(toKey(cs));
                      if (!os) return true; // new service line inside existing guest
                      if (
                        Number(os.quantity) !== Number(cs.quantity) ||
                        Number(os.rate) !== Number(cs.rate)
                      )
                        return true; // modified values
                    }
                    return false;
                  },
                );

                // Always send full current list (ensure backend processes new + existing guests uniformly)
                // Filter out incomplete guests for submission - only include guests with minimum required data
                return workingSubCustomers
                  .filter((sub) => {
                    // For accommodation services, require complete guest data
                    if (hasAccommodationServices()) {
                      return (
                        sub.customerName &&
                        sub.customerCheckIn &&
                        sub.customerCheckOut
                      );
                    }
                    // For venue services, just require guest name
                    return sub.customerName;
                  })
                  .map((sub) => {
                    return {
                      customerID: sub.customerID || 0, // ⭐ FIX: Include customerID for existing subcustomers!
                      roomId: deriveRoomId(sub),
                      roomID: deriveRoomId(sub),
                      companyID: companyForm.companyID || 0,
                      customerName: sub.customerName,
                      orderID: 0,
                      customerEmail: sub.customerEmail || "", // ⭐ FIX: Ensure empty string instead of null/undefined
                      customerCheckIn: sub.customerCheckIn,
                      customerCheckOut: sub.customerCheckOut,
                      services: sub.services || [],
                      totalAmount: guestTotal(sub),
                    };
                  });
              })()
            : [],
        orderID: isEditing ? data.orderID || data.orderId : 0,
        createdBy: user.id,
        clientType: customerType,
        // Provide both key casings to maintain backward compatibility with legacy API expectations
        subTotal: subtotal,
        SubTotal: subtotal,
        outletId: 4,
        statusId: statusId,
        checkInDate: overallCheckIn,
        checkOutDate: overallCheckOut,
        phoneNumber:
          customerType === "individual" ? customerForm.phoneNumber : "",
        email:
          customerType === "individual"
            ? customerForm.email
            : companyForm.companyEmail || "",
        // Include additional customer details for new customers
        salutation:
          customerType === "individual" ? customerForm.salutation : "",
        countryCode:
          customerType === "individual" ? customerForm.countryCode : "",
        // New: optionalEmail carries the quotation recipient when it's a quotation (statusId 11)
        optionalEmail: statusId === 11 ? paymentData.quotationEmail || "" : "",
        ccEmail: statusId === 11 ? paymentData.ccEmail || "" : "",
        BookingType:
          paymentData.documentType ||
          (paymentData.createQuotation ? docSendType : "Proforma"),
        discount: Number(paymentData.discount || 0),
        discountType: Number(paymentData.discountType || 0),
        paymentDate: new Date().toISOString(),
        // Quotation: no charges; On-account: shift to payAmount and set paymentType 4; else normal
        receivedAmount: isQuotation
          ? 0
          : billToAccount
            ? 0
            : Number(paymentData.paidAmount || 0),
        payAmount: isQuotation
          ? 0
          : billToAccount
            ? Number(paymentData.grossTotal || subtotal)
            : 0,
        paymentType: Number(
          paymentData.paymentType !== undefined
            ? paymentData.paymentType
            : billToAccount
              ? 4
              : 0,
        ),
        paymentNote: paymentData.notes || "",
        transactionId: "",
        utrNumber: paymentData.utrNumber || "",
        financialYearId: 0,
        isDeleted: 0,
        payNow: isQuotation
          ? 0
          : billToAccount
            ? 0
            : Number(paymentData.paidAmount || 0) > 0
              ? 1
              : 0,
        roomQuantity: [...selectedRooms, ...selectedChildren].reduce(
          (acc, item) => acc + (item.quantity || 0),
          0,
        ),
        serviceDetails: allServices,
        // Number of guests - ALWAYS include for company venue bookings
        numberOfGuests: (() => {
          // Use proper null checking to preserve 0 values
          const guestCount =
            numberOfGuests !== undefined && numberOfGuests !== null
              ? numberOfGuests
              : 1;

          // For individual bookings with parentId 218 services (accommodation)
          if (customerType === "individual" && hasParentId218Services()) {
            return guestCount;
          }

          // For individual venue bookings - Include for venue bookings too
          if (customerType === "individual" && hasVenueServices()) {
            return guestCount;
          }

          // For company venue bookings (no guest cards) - ALWAYS include numberOfGuests
          if (customerType === "company" && subCustomers.length === 0) {
            return guestCount;
          }

          // For company accommodation bookings, guests are handled individually
          return undefined; // Use undefined instead of null to avoid filtering issues
        })(),
        // WORKAROUND: Add numberOfGuests in multiple formats to ensure pickup
        number_of_guests: (() => {
          const guestCount =
            numberOfGuests !== undefined && numberOfGuests !== null
              ? numberOfGuests
              : 1;

          if (customerType === "company" && subCustomers.length === 0) {
            console.log(
              "🔍 [DEBUG] Setting number_of_guests for company:",
              guestCount,
            );
            return guestCount;
          }
          if (customerType === "individual" && hasParentId218Services()) {
            console.log(
              "🔍 [DEBUG] Setting number_of_guests for individual accommodation:",
              guestCount,
            );
            return guestCount;
          }
          if (customerType === "individual" && hasVenueServices()) {
            console.log(
              "🔍 [DEBUG] Setting number_of_guests for individual venue:",
              guestCount,
            );
            return guestCount;
          }
          return undefined;
        })(),
        NumberOfGuests: (() => {
          const guestCount =
            numberOfGuests !== undefined && numberOfGuests !== null
              ? numberOfGuests
              : 1;

          if (customerType === "company" && subCustomers.length === 0) {
            console.log(
              "🔍 [DEBUG] Setting NumberOfGuests for company:",
              guestCount,
            );
            return guestCount;
          }
          if (customerType === "individual" && hasParentId218Services()) {
            console.log(
              "🔍 [DEBUG] Setting NumberOfGuests for individual accommodation:",
              guestCount,
            );
            return guestCount;
          }
          if (customerType === "individual" && hasVenueServices()) {
            console.log(
              "🔍 [DEBUG] Setting NumberOfGuests for individual venue:",
              guestCount,
            );
            return guestCount;
          }
          return undefined;
        })(),
        guestCount: (() => {
          const guestCount =
            numberOfGuests !== undefined && numberOfGuests !== null
              ? numberOfGuests
              : 1;

          if (customerType === "company" && subCustomers.length === 0) {
            console.log(
              "🔍 [DEBUG] Setting guestCount for company:",
              guestCount,
            );
            return guestCount;
          }
          if (customerType === "individual" && hasParentId218Services()) {
            console.log(
              "🔍 [DEBUG] Setting guestCount for individual accommodation:",
              guestCount,
            );
            return guestCount;
          }
          if (customerType === "individual" && hasVenueServices()) {
            console.log(
              "🔍 [DEBUG] Setting guestCount for individual venue:",
              guestCount,
            );
            return guestCount;
          }
          return undefined;
        })(),
        // Exceed limit flag required by backend ("yes" when limit exceeded)
        Exceed_Limit:
          paymentData?.exceedLimitFlag === true ||
          paymentData?.Exceed_Limit === "yes"
            ? "yes"
            : "no",
      };

      // Debug: Clean final payload review
      console.log("🔍 [DEBUG] === FINAL GUEST COUNT VERIFICATION ===");
      console.log("🔍 [DEBUG] numberOfGuests field:", orderBody.numberOfGuests);
      console.log(
        "🔍 [DEBUG] number_of_guests field:",
        orderBody.number_of_guests,
      );
      console.log("🔍 [DEBUG] NumberOfGuests field:", orderBody.NumberOfGuests);
      console.log("🔍 [DEBUG] guestCount field:", orderBody.guestCount);
      console.log("🔍 [DEBUG] Current numberOfGuests state:", numberOfGuests);
      console.log("🔍 [DEBUG] ==========================================");

      // Commented out verbose logging for cleaner debug output
      // console.log("=== FINAL ORDER BODY BEFORE SUBMIT ===");
      // console.log("orderBody.numberOfGuests:", orderBody.numberOfGuests);
      // console.log("orderBody.number_of_guests:", orderBody.number_of_guests);
      // console.log("orderBody.NumberOfGuests:", orderBody.NumberOfGuests);
      // console.log("orderBody.guestCount:", orderBody.guestCount);
      // console.log("Full orderBody keys:", Object.keys(orderBody));
      // console.log(
      //   "orderBody serviceDetails length:",
      //   orderBody.serviceDetails?.length
      // );
      // console.log("=====================================");

      await onSubmit(orderBody, {
        resendDocument: paymentData?.resendDocument,
        sendResendAfterSave: paymentData?.sendResendAfterSave,
      });
    } catch (err) {
      console.error("submitOrder error:", err);
      toast.error("Something went wrong. Please try again.");
    }
  };

  // Fix Save & Continue logic for company bookings
  const isFormValid = () => {
    const hasServices =
      customerType === "company"
        ? subCustomers.some((sc) => sc.services && sc.services.length > 0) ||
          selectedRooms.length > 0 ||
          selectedChildren.length > 0
        : selectedRooms.length > 0 || selectedChildren.length > 0;

    if (customerType === "individual") {
      // Existing individual validation logic
      const dateFieldType = getDateFieldType();

      let dateValidation;
      if (dateFieldType === "dual") {
        dateValidation = checkInDate && checkOutDate;
      } else {
        dateValidation = selectedDate || checkInDate;
      }

      const guestValidation = hasParentId218Services()
        ? numberOfGuests >= 1
        : true;

      const venueAvailabilityValidation = (() => {
        if (dateFieldType === "single" && !hasAccommodationServices()) {
          const hasVenueServices = [...selectedRooms, ...selectedChildren].some(
            (service) => !isAccommodation(service),
          );

          if (hasVenueServices) {
            const isNewSale = !data;

            if (isNewSale) {
              return venuesAvailable !== false;
            } else {
              const currentStatusID = Number(data?.statusID || 0);
              const isConfirmedBooking = currentStatusID !== 7;

              if (isConfirmedBooking) {
                const hasChanges = shouldCheckVenueAvailability();

                if (!hasChanges) {
                  return true;
                }

                if (checkingVenueAvailability) {
                  return true;
                }

                if (venuesAvailable === false) {
                  return false;
                }

                return true;
              }

              if (checkingVenueAvailability) {
                return true;
              }
              if (venuesAvailable === false) {
                return false;
              }
            }
          }
        }
        return true;
      })();

      const basicValidation =
        customerForm.customerName &&
        customerForm.phoneNumber &&
        dateValidation &&
        hasServices &&
        guestValidation &&
        venueAvailabilityValidation;

      return basicValidation;
    }

    // Company booking validation
    const companyBasicValidation = companyForm.companyName && hasServices;

    // If guest cards are added, validate them (regardless of service type)
    if (subCustomers.length > 0) {
      // For company bookings, only validate that at least one guest has basic info
      // This allows adding multiple guests without requiring all to be complete
      const hasAtLeastOneValidGuest = subCustomers.some(
        (s) =>
          s.customerName &&
          s.customerCheckIn &&
          s.customerCheckOut &&
          s.customerCheckIn < s.customerCheckOut,
      );

      // For company bookings with accommodation services, require at least one complete guest
      if (hasAccommodationServices() && !hasAtLeastOneValidGuest) {
        return false;
      }

      // For venue bookings, less strict validation - just need guest names
      if (hasVenueServices() && !subCustomers.some((s) => s.customerName)) {
        return false;
      }
    }

    // For accommodation bookings with guests, require at least one guest
    if (hasAccommodationServices() && subCustomers.length === 0) {
      // Accommodation services selected but no guests added - invalid
      return false;
    }

    // For venue bookings (without accommodation), just need company details, date, and services
    if (hasVenueServices() && !hasAccommodationServices()) {
      const venueValidation = selectedDate && numberOfGuests >= 1;

      // Venue availability check for company bookings
      const venueAvailabilityValidation = (() => {
        const isNewSale = !data;

        if (isNewSale) {
          return venuesAvailable !== false;
        } else {
          const currentStatusID = Number(data?.statusID || 0);
          const isConfirmedBooking = currentStatusID !== 7;

          if (isConfirmedBooking) {
            const hasChanges = shouldCheckVenueAvailability();

            if (!hasChanges) {
              return true;
            }

            if (checkingVenueAvailability) {
              return true;
            }

            if (venuesAvailable === false) {
              return false;
            }

            return true;
          }

          if (checkingVenueAvailability) {
            return true;
          }
          if (venuesAvailable === false) {
            return false;
          }
        }
        return true;
      })();

      return (
        companyBasicValidation && venueValidation && venueAvailabilityValidation
      );
    }

    return companyBasicValidation;
  };

  // Payment form submit
  const handlePaymentSubmit = async (paymentData) => {
    setStoredPaymentData(paymentData);
    const resendInfo = paymentData?.resendDocument || {};

    if (paymentData.createQuotation === true) {
      setEmailModal(true); // open email modal to capture quotation email
      setPaymentFormModal(false);
      return;
    }

    try {
      // Normal (non account) bookings never exceed limit flag
      await submitOrder(
        { ...paymentData, quotationEmail: undefined, exceedLimitFlag: false },
        7,
      ); // normal reservation default status 7

      setPaymentFormModal(false);
    } catch (err) {
      console.error("Payment submission failed:", err);
      toast.error("Something went wrong while saving the booking.");
    }
  };

  // Quotation email submit
  const submitQuotation = () => {
    const stored = getStoredPaymentData() || {};
    if (!stored?.createQuotation) {
      submitOrder({ ...stored, quotationEmail: undefined }, 7);
      setEmailModal(false);
      return;
    }
    const primary = quotationEmail.trim();
    const cc = ccEmail.trim();
    if (!primary) {
      toast.error("Please enter an email address");
      return;
    }
    const emailRegex = /[^@\s]+@[^@\s]+\.[^@\s]+/;
    if (!emailRegex.test(primary)) {
      toast.error("Primary email invalid");
      return;
    }
    if (cc && !emailRegex.test(cc)) {
      toast.error("CC email invalid");
      return;
    }
    const statusForDoc = 11; // same status for quotation & proforma (adjust if backend differentiates)
    submitOrder(
      {
        ...stored,
        quotationEmail: primary, // main recipient
        ccEmail: cc || "", // pass separately so orderBody can map to optionalEmail
        createQuotation: true,
        documentType: docSendType,
        exceedLimitFlag: stored?.exceedLimitFlag === true,
      },
      statusForDoc,
    );
    setEmailModal(false);
  };

  const buildResendDocumentPayload = (resendInfo = {}) => {
    const orderId = data?.orderID || data?.orderId || 0;
    const orderNumber =
      data?.orderNumber ||
      data?.orderNo ||
      data?.order_no ||
      data?.orderNoText ||
      data?.orderNumberText ||
      orderId;

    return {
      orderId,
      orderNumber,
      outletId: user?.outletID || user?.outletId || 0,
      documentType: resendInfo?.documentType || "proforma",
      email: resendInfo?.email || "",
      ccEmail: resendInfo?.ccEmail || "",
      source: "resend-document",
    };
  };

  const [storedPaymentData, setStoredPaymentData] = useState(null);
  const getStoredPaymentData = () => storedPaymentData;

  // Hydrate edit view from API data (GetReservations3)
  useEffect(() => {
    if (!data) return;
    try {
      const api = data || {};

      // Determine booking type
      const isCompany = Number(api.companyID ?? api.companyId ?? 0) > 0;

      console.log("=== EDIT MODE HYDRATION ===");
      console.log("api.companyID:", api.companyID);
      console.log("api.companyId:", api.companyId);
      console.log("isCompany:", isCompany);

      setCustomerType(isCompany ? "company" : "individual");

      // Normalize dates
      const toDate = (d) => (typeof d === "string" ? d.split("T")[0] : d);
      const orderDate = toDate(api.orderDate);
      const checkOutDateFromApi = toDate(api.checkOutDate);

      setCheckInDate(orderDate || null);
      setCheckOutDate(checkOutDateFromApi || null);

      // Set selectedDate for venue services (single date scenarios)
      if (orderDate) {
        setSelectedDate(orderDate);
      }

      // Prefill customer/company details
      if (!isCompany) {
        console.log("=== HYDRATING INDIVIDUAL BOOKING ===");
        setCustomerForm((prev) => ({
          ...prev,
          customerName: api.customerName || "",
          phoneNumber: api.customerNumber || api.phoneNumber || "",
          email: api.emails || api.email || "",
          isCustomerSelected: Boolean(api.customerID || api.customerId),
          customerID: api.customerID || api.customerId || null,
        }));
        if (api.customerID || api.customerId) {
          setSelectedCustomer({
            customerID: api.customerID || api.customerId,
            customerName: api.customerName || "",
            phoneNumber: api.customerNumber || api.phoneNumber || "",
            email: api.email || "",
          });
        }
      } else {
        console.log("=== HYDRATING COMPANY BOOKING ===");
        setCompanyForm((prev) => ({
          ...prev,
          companyName: api.companyName || "",
          companyID: api.companyID || api.companyId || null,
          companyAddress: api.companyAddress || api.addresses || "",
          companyContact: api.companyContact || api.customerNumber || "",
          companyEmail: api.companyEmail || api.emails || "",
          isCompanySelected: Boolean(api.companyID || api.companyId),
        }));
      }

      // 🔧 NEW: Check for existing account payments and set Bill to Account state
      // Since paymentData prop is manually constructed, we need to check for paymentType directly
      console.log("💳 Checking for account payments in edit mode");
      console.log("paymentData prop:", paymentData);

      // Check if the single payment (from paymentData prop) is an account payment
      const hasAccountPayment =
        paymentData &&
        (Number(paymentData.paymentType) === 4 ||
          Number(paymentData.paymentType) === 10001);

      if (hasAccountPayment) {
        console.log(
          "✅ Found account payment (paymentType: 4 or 10001) - setting Bill to Account as checked",
        );
        console.log("Payment details:", {
          paymentType: paymentData.paymentType,
          notes: paymentData.notes,
          amount: paymentData.paidAmount,
        });
        setIsOnAccount(true);

        // 🔧 NEW: Trigger credit info fetch for the customer/company
        const customerID = api.customerID || api.customerId;
        const companyID = api.companyID || api.companyId;

        if (!isCompany && customerID) {
          console.log(
            "🔍 Account payment detected - customer credit info should already be loaded",
          );
          // Credit info should already be loaded through existing customer hydration
          // The customerApi useQuery will automatically fetch credit details since customerForm.customerName is set
        } else if (isCompany && companyID) {
          console.log(
            "🔍 Account payment detected - company credit info should already be loaded",
          );
          // Company credit will be fetched through existing mechanisms
        }
      } else {
        console.log(
          "ℹ️ No account payment found - keeping Bill to Account unchecked",
        );
        console.log("Payment type found:", paymentData?.paymentType);
        setIsOnAccount(false);
      }

      // Map services to UI selections
      const rawServices =
        api.serviceTypeAndName || api.services || api.ServiceTypeAndName || [];
      if (!Array.isArray(rawServices)) return;

      console.log("Raw services from API:", rawServices);

      const toBase = (s) => {
        const typeName =
          s.service_type_name || s.serviceName || s.typeName || "";
        const rate = Number(
          s.servicePrice ?? s.rate ?? s.service_rate ?? s.price ?? 0,
        );
        const qtyRaw = Number(
          s.serviceQuantity ?? s.quantity ?? s.service_qty ?? 1,
        );
        const qty = qtyRaw > 0 ? qtyRaw : 1;

        const keyId = Number(
          s.orderDetailPrimaryID ??
            s.orderDetailId ??
            s.keyID ??
            s.keyId ??
            s.id ??
            s.ID ??
            0,
        );

        // FIXED: Properly map serviceId vs serviceTypeId
        const originalServiceId = Number(s.serviceId ?? s.serviceID ?? 0);
        const serviceTypeId = Number(
          s.serviceTypeId ?? s.service_type_id ?? s.typeId ?? 0,
        );

        const roomId =
          s.roomId !== null && s.roomId !== undefined
            ? Number(s.roomId)
            : s.room_id !== null && s.room_id !== undefined
              ? Number(s.room_id)
              : 0;

        // 🔧 FIX: Never substitute roomId for the order_details primary key.
        // roomId identifies which physical room was booked; keyId is the
        // actual order_details row id the backend now upserts by. Conflating
        // them caused accommodation lines to be saved with the wrong
        // OrderDetailPrimaryID, which could collide with (and overwrite) an
        // unrelated line's row once the backend switched to upsert-by-ID.
        const orderDetailID = keyId;

        // Use serviceTypeId for childId/typeId (catalog reference)
        const childId = serviceTypeId;

        // Debug log only for venue services to verify mapping
        if (childId === 536) {
          console.log("🔍 Mapping venue service:", {
            childId,
            typeName,
            rate,
          });
        }

        return {
          childId,
          typeId: serviceTypeId,
          serviceTypeId, // Keep serviceTypeId separate
          typeName,
          productServiceName: s.productServiceName || s.code || typeName,
          parentId: Number(s.parentId ?? s.parentID ?? 0),
          rate: Number(rate),
          quantity: Number(qty),
          maxQuantity: qty,
          keyId,
          roomId,
          orderDetailPrimaryID: orderDetailID,
          originalServiceId, // FIXED: Store the original serviceId separately
          serviceId: originalServiceId, // Keep original serviceId for backend
        };
      };

      if (!isCompany) {
        // Individual booking hydration (existing logic)
        const items = rawServices.map(toBase);
        const rooms = [];
        const others = [];
        items.forEach((it) => {
          const probe = {
            typeName: it.typeName,
            productServiceName: it.productServiceName,
          };
          if (isAccommodation(probe)) rooms.push(it);
          else others.push(it);
        });
        setSelectedRooms(rooms);
        setSelectedChildren(others);

        console.log("🏨 Individual booking hydration complete:");
        console.log("  - Accommodation services (rooms):", rooms.length);
        console.log("  - Venue services (children):", others.length);
        console.log(
          "  - Selected service childIds:",
          others.map((o) => o.childId),
        );

        // Set numberOfGuests if available from API for individual bookings
        const guestCount = Number(
          api.numberOfGuests || api.guest_count || api.number_of_guests || 1,
        );
        if (guestCount > 1) {
          setNumberOfGuests(guestCount);
          console.log(
            "🔄 Restored numberOfGuests for individual booking:",
            guestCount,
          );
        }

        // Capture original venue details for optimization
        const currentVenueServices = [...rooms, ...others].filter(
          (service) => !isAccommodation(service),
        );

        setOriginalVenueDetails({
          selectedDate: orderDate || null,
          venueServices: currentVenueServices.map((s) => ({
            childId: s.childId,
            parentId: s.parentId,
            typeId: s.typeId,
          })),
          isEdit: true,
          hydrationComplete: true,
        });

        // Store original data for tracking changes
        setOriginalItemCount(rawServices.length);
        setOriginalIndividualServices(
          [...rooms, ...others].map((it) => ({
            keyID: Number(it.keyId || it.keyID || 0),
            roomID: Number(it.roomId || it.roomID || 0),
            childId: Number(it.childId),
            quantity: Number(it.quantity || 1),
            rate: Number(it.rate || 0),
            typeId: Number(it.typeId || 0),
            parentId: Number(it.parentId || 0),
          })),
        );
        setOriginalIndividualKeyList(
          [...rooms, ...others].map(
            (it) =>
              `c:${Number(it.childId)}-t:${Number(it.typeId)}-p:${Number(
                it.parentId || 0,
              )}-r:${Number(it.rate)}`,
          ),
        );
      } else {
        // Company booking hydration
        console.log("=== COMPANY BOOKING HYDRATION ===");

        // 🔧 ENHANCED: Better logic to detect accommodation vs venue company bookings
        // Especially important for contractor companies with dedicated accommodations
        const hasSubCustomerData = rawServices.some(
          (s) =>
            s.subCustomerId ||
            s.sub_customer_id ||
            s.subCustomerName ||
            s.sub_customer_name ||
            s.subCustomerEmail ||
            s.sub_customer_email ||
            s.subCustomerCheckIn ||
            s.sub_customer_check_in ||
            s.subCustomerCheckOut ||
            s.sub_customer_check_out,
        );

        // 🔧 NEW: Check if services are accommodation-type (especially for contractor accommodations)
        const hasAccommodationServices = rawServices.some((s) => {
          const serviceBase = {
            typeName: s.service_type_name || s.serviceName || s.typeName || "",
            productServiceName: s.productServiceName || s.code || "",
          };

          // Check using the accommodation detection function
          const isAccomm = isAccommodation(serviceBase);

          // ADDITIONAL: Check if it's an accommodation parentId (212 for accommodation)
          const isAccommByParentId =
            Number(s.parentId || s.parentID || 0) === 212;

          // ADDITIONAL: Check for contractor accommodation patterns
          const serviceName = serviceBase.typeName.toLowerCase();
          const productName = serviceBase.productServiceName.toLowerCase();
          const isContractorAccomm =
            serviceName.includes("contractor") ||
            serviceName.includes("staff") ||
            serviceName.includes("employee") ||
            productName.includes("contractor") ||
            productName.includes("staff") ||
            productName.includes("employee");

          return isAccomm || isAccommByParentId || isContractorAccomm;
        });

        // 🔧 NEW: Enhanced check - if services are accommodation-type AND have guest assignments
        const hasAccommodationWithGuests = rawServices.some((s) => {
          const serviceBase = {
            typeName: s.service_type_name || s.serviceName || s.typeName || "",
            productServiceName: s.productServiceName || s.code || "",
          };
          const isAccomm = isAccommodation(serviceBase);
          const isAccommByParentId =
            Number(s.parentId || s.parentID || 0) === 212;
          const hasGuestInfo =
            s.subCustomerId ||
            s.sub_customer_id ||
            s.subCustomerName ||
            s.sub_customer_name;
          return (isAccomm || isAccommByParentId) && hasGuestInfo;
        });

        // 🔧 NEW: Check for dual-date structure (accommodation characteristic)
        const hasDualDateStructure = Boolean(
          api.orderDate &&
          api.checkOutDate &&
          api.orderDate !== api.checkOutDate,
        );

        console.log("🔍 Enhanced company booking detection:");
        console.log("  - hasSubCustomerData:", hasSubCustomerData);
        console.log("  - hasAccommodationServices:", hasAccommodationServices);
        console.log(
          "  - hasAccommodationWithGuests:",
          hasAccommodationWithGuests,
        );
        console.log("  - hasDualDateStructure:", hasDualDateStructure);
        console.log(
          "  - Raw services:",
          rawServices.map((s) => ({
            serviceName: s.service_type_name || s.serviceName,
            productName: s.productServiceName,
            parentId: s.parentId,
            subCustomerId: s.subCustomerId,
            subCustomerName: s.subCustomerName,
            orderDate: api.orderDate,
            checkOutDate: api.checkOutDate,
          })),
        );

        // 🔧 ENHANCED: More intelligent decision logic
        // For contractor accommodations, we should create guest cards even if subcustomer data is sparse
        const shouldCreateAccommodationBooking =
          hasSubCustomerData ||
          hasAccommodationWithGuests ||
          (hasAccommodationServices && hasDualDateStructure);

        console.log(
          "🎯 Final decision - shouldCreateAccommodationBooking:",
          shouldCreateAccommodationBooking,
        );

        if (shouldCreateAccommodationBooking) {
          // Company accommodation booking with guest cards
          console.log(
            "✅ Detected: Company accommodation booking with guests (including contractor accommodations)",
          );

          // Use ACCOMMODATION services as demarcation points to group guests
          const guests = [];
          let currentGuest = null;

          // 🔧 FIX: Match a non-accommodation service row to the guest it actually
          // belongs to (by subCustomerId / subCustomerName / roomId) instead of
          // assuming the raw services array is strictly ordered per-guest. The
          // backend does not guarantee that every guest's rows are contiguous,
          // so blindly appending to `currentGuest` can collapse services from
          // multiple guests onto whichever guest happened to be processed last.
          const findGuestForService = (row, builtGuests, fallbackGuest) => {
            const candidates = fallbackGuest
              ? [...builtGuests, fallbackGuest]
              : builtGuests;
            if (candidates.length === 0) return null;

            const rowSubCustomerId = Number(
              row.subCustomerId || row.sub_customer_id || 0,
            );
            const rowSubCustomerName = (
              row.subCustomerName ||
              row.sub_customer_name ||
              ""
            ).trim();
            const rowRoomId = Number(row.roomId || row.room_id || 0);

            // Try matching by an identifying key first, REGARDLESS of how
            // many candidates exist. A row belonging to a brand-new guest
            // must not be blindly attached to an existing single guest just
            // because only one guest has been built so far (e.g. guest #2's
            // very first accommodation row, while only guest #1 exists yet).
            if (rowSubCustomerId) {
              const match = candidates.find(
                (g) => Number(g.customerID) === rowSubCustomerId,
              );
              if (match) return match;
              // Has a subCustomerId but it doesn't match any known guest -
              // this genuinely belongs to a different/new guest.
              return null;
            }
            if (rowSubCustomerName) {
              const match = candidates.find(
                (g) => (g.customerName || "").trim() === rowSubCustomerName,
              );
              if (match) return match;
              return null;
            }
            if (rowRoomId) {
              const match = candidates.find(
                (g) => Number(g.roomID) === rowRoomId,
              );
              if (match) return match;
              return null;
            }

            // No identifying key at all on this row (legacy/older data) -
            // only then fall back to "there's just one guest so far".
            if (candidates.length === 1) return candidates[0];
            return null;
          };

          rawServices.forEach((s) => {
            const base = toBase(s);
            const isAccommByFunction = isAccommodation({
              typeName: base.typeName,
              productServiceName: base.productServiceName,
            });

            const isAccommByProduct = (s.productServiceName || "")
              .toLowerCase()
              .includes("accommodation");
            const isAccommByParentId =
              Number(s.parentId || s.parentID || 0) === 212;

            // 🔧 FIX: Do NOT treat "has subcustomer data" as "is accommodation".
            // Every order-detail row (accommodation AND regular services) now
            // reliably carries SubCustomerID/subCustomerName, so using its
            // mere presence to decide "this is a Room" mis-tagged every
            // regular service as accommodation and made the loop below spawn
            // a brand-new duplicate guest card for each one. Whether a row is
            // an accommodation product must be judged purely by the product
            // itself (type/parent/name), never by guest-linking fields.
            const isAccomm =
              isAccommByFunction || isAccommByProduct || isAccommByParentId;

            console.log(
              `🔍 Processing service: ${base.typeName} (${base.productServiceName})`,
            );
            console.log(`  - isAccommByFunction: ${isAccommByFunction}`);
            console.log(`  - isAccommByProduct: ${isAccommByProduct}`);
            console.log(`  - isAccommByParentId: ${isAccommByParentId}`);
            console.log(`  - Final isAccommodation: ${isAccomm}`);

            // 🔧 FIX: Resolve the guest this row belongs to by its own
            // guest-linking key (subCustomerId/subCustomerName/roomId) FIRST,
            // regardless of whether the row is accommodation or a regular
            // service. Only create a brand-new guest card when no existing
            // guest matches - this stops a guest's 2nd+ service (of any kind,
            // including a 2nd accommodation line) from spawning a duplicate
            // guest card just because it happens to be accommodation-typed.
            let targetGuest = findGuestForService(s, guests, currentGuest);

            if (!targetGuest) {
              // Save the previous in-progress guest before starting a new one
              if (currentGuest && currentGuest.services.length > 0) {
                guests.push(currentGuest);
                console.log(
                  `📝 Saved previous guest: ${currentGuest.customerName} with ${currentGuest.services.length} services`,
                );
              }

              const subCustomerId = Number(
                s.subCustomerId || s.sub_customer_id || 0,
              );
              const customerName =
                s.subCustomerName || s.sub_customer_name || "";

              console.log(
                `🆕 Creating new guest: ${customerName} (ID: ${subCustomerId})`,
              );

              currentGuest = {
                customerID: subCustomerId,
                customerName: customerName || `Guest ${guests.length + 1}`,
                customerEmail: s.subCustomerEmail || s.sub_customer_email || "",
                customerCheckIn:
                  toDate(s.subCustomerCheckIn || s.sub_customer_check_in) ||
                  toDate(api.orderDate),
                customerCheckOut:
                  toDate(s.subCustomerCheckOut || s.sub_customer_check_out) ||
                  toDate(api.checkOutDate),
                availability: null,
                services: [],
                roomID: Number(s.roomId || s.room_id || 0),
              };
              targetGuest = currentGuest;
            }

            targetGuest.services.push({
              childId: base.childId,
              rate: base.rate,
              quantity: base.quantity,
              typeId: base.typeId,
              serviceTypeId: base.serviceTypeId,
              serviceId: base.originalServiceId || base.serviceId,
              originalServiceId: base.originalServiceId,
              name: base.productServiceName || base.typeName,
              rawName: base.typeName || base.productServiceName,
              accommodation: isAccomm,
              parentId: base.parentId || 0,
              keyId: base.keyId || 0,
              roomId: base.roomId || 0,
            });

            console.log(
              `➕ Added service to guest "${targetGuest.customerName}": ${base.typeName} (accommodation: ${isAccomm})`,
            );
          });

          // Don't forget to add the last guest
          if (currentGuest && currentGuest.services.length > 0) {
            guests.push(currentGuest);
            console.log(
              `📝 Added final guest: ${currentGuest.customerName} with ${currentGuest.services.length} services`,
            );
          }

          console.log(
            `🎯 Final guest count after processing: ${guests.length} guests from ${rawServices.length} service records`,
          );

          // 🔧 ENHANCED: Special handling for contractor accommodations without explicit subcustomer data
          if (guests.length === 0 && hasAccommodationServices) {
            console.log(
              "⚠️ Creating accommodation guests for contractor booking without explicit subcustomer data",
            );

            const accommodationServices = rawServices.filter((s) => {
              const serviceBase = {
                typeName:
                  s.service_type_name || s.serviceName || s.typeName || "",
                productServiceName: s.productServiceName || s.code || "",
              };
              return (
                isAccommodation(serviceBase) ||
                Number(s.parentId || s.parentID || 0) === 212
              );
            });

            // Group by room or create individual guests for each accommodation service
            accommodationServices.forEach((s, index) => {
              const base = toBase(s);
              const guestName =
                s.subCustomerName ||
                s.sub_customer_name ||
                `${companyForm.companyName || api.companyName} - Guest ${
                  index + 1
                }`;

              guests.push({
                customerID: Number(s.subCustomerId || s.sub_customer_id || 0),
                customerName: guestName,
                customerEmail:
                  s.subCustomerEmail ||
                  s.sub_customer_email ||
                  companyForm.companyEmail ||
                  api.companyEmail ||
                  "",
                customerCheckIn:
                  toDate(s.subCustomerCheckIn || s.sub_customer_check_in) ||
                  toDate(api.orderDate),
                customerCheckOut:
                  toDate(s.subCustomerCheckOut || s.sub_customer_check_out) ||
                  toDate(api.checkOutDate),
                availability: null,
                services: [
                  {
                    childId: base.childId,
                    rate: base.rate,
                    quantity: base.quantity,
                    typeId: base.typeId,
                    serviceTypeId: base.serviceTypeId,
                    serviceId: base.originalServiceId || base.serviceId,
                    originalServiceId: base.originalServiceId,
                    name: base.productServiceName || base.typeName,
                    rawName: base.typeName || base.productServiceName,
                    accommodation: true,
                    parentId: base.parentId || 0,
                    keyId: base.keyId || 0,
                    roomId: base.roomId || 0,
                  },
                ],
                roomID: Number(s.roomId || s.room_id || 0),
              });
            });

            // Add any non-accommodation services to the first guest
            const nonAccommodationServices = rawServices.filter((s) => {
              const serviceBase = {
                typeName:
                  s.service_type_name || s.serviceName || s.typeName || "",
                productServiceName: s.productServiceName || s.code || "",
              };
              return (
                !isAccommodation(serviceBase) &&
                Number(s.parentId || s.parentID || 0) !== 212
              );
            });

            if (guests.length > 0 && nonAccommodationServices.length > 0) {
              nonAccommodationServices.forEach((s) => {
                const base = toBase(s);
                // 🔧 FIX: Match by subCustomerId/subCustomerName/roomId instead
                // of unconditionally dumping every extra service onto
                // guests[0] - only fall back to guests[0] when no
                // identifying key is present on the row (single-guest case
                // still resolves to guests[0] via findGuestForService).
                const targetGuest =
                  findGuestForService(s, guests, null) || guests[0];
                targetGuest.services.push({
                  childId: base.childId,
                  rate: base.rate,
                  quantity: base.quantity,
                  typeId: base.typeId,
                  serviceTypeId: base.serviceTypeId,
                  serviceId: base.originalServiceId || base.serviceId,
                  originalServiceId: base.originalServiceId,
                  name: base.productServiceName || base.typeName,
                  rawName: base.typeName || base.productServiceName,
                  accommodation: false,
                  parentId: base.parentId || 0,
                  keyId: base.keyId || 0,
                  roomId: base.roomId || 0,
                });
              });
            }
          }

          guests.forEach((g) => {
            g.services = dedupeGuestServices(g.services);
          });

          console.log(
            `📊 Company accommodation hydration complete: ${guests.length} guests created from ${rawServices.length} service records`,
          );

          console.log(
            "Guest details:",
            guests.map((g) => ({
              name: g.customerName,
              email: g.customerEmail,
              checkIn: g.customerCheckIn,
              checkOut: g.customerCheckOut,
              serviceCount: g.services.length,
              isContractorGuest: g.customerName.includes(
                companyForm.companyName || api.companyName || "",
              ),
            })),
          );

          // Debug logging for troubleshooting subtotal issues with many guests
          console.log(
            `📊 Hydration complete: ${guests.length} guests created from ${rawServices.length} service records`,
          );
          console.log(
            "Guest breakdown:",
            guests.map((g, i) => ({
              index: i,
              name: g.customerName,
              serviceCount: g.services.length,
              checkIn: g.customerCheckIn,
              checkOut: g.customerCheckOut,
            })),
          );

          setSubCustomers(guests);
          setOriginalSubCustomers(JSON.parse(JSON.stringify(guests)));
          setActiveGuestIndex(guests.length ? 0 : null);
          setOriginalItemCount(guests.length);

          // For guest-based company bookings, also populate selection arrays for subtotal calculation
          const allServices = guests.flatMap((g) => g.services || []);
          const rooms = [];
          const others = [];

          allServices.forEach((service) => {
            const probe = {
              typeName: service.rawName,
              productServiceName: service.name,
            };
            if (isAccommodation(probe)) {
              rooms.push(service);
            } else {
              others.push(service);
            }
          });

          setSelectedRooms(rooms);
          setSelectedChildren(others);
        } else {
          // Company venue booking (no guest cards)
          console.log("✅ Detected: Company venue booking (no guest cards)");

          const items = rawServices.map(toBase);
          const rooms = [];
          const others = [];

          items.forEach((it) => {
            const probe = {
              typeName: it.typeName,
              productServiceName: it.productServiceName,
            };
            if (isAccommodation(probe)) {
              rooms.push(it);
            } else {
              others.push(it);
            }
          });

          // For company venue bookings, populate selectedRooms and selectedChildren directly
          // This ensures the UI shows the services and subtotal calculates correctly
          setSelectedRooms(rooms);
          setSelectedChildren(others);

          console.log(
            "Company venue booking - rooms:",
            rooms.length,
            "others:",
            others.length,
          );

          // Set numberOfGuests if available from API
          const guestCount = Number(api.numberOfGuests || api.guest_count || 1);
          if (guestCount > 1) {
            setNumberOfGuests(guestCount);
          }

          // Store original data for tracking changes
          setOriginalItemCount(rawServices.length);
          setOriginalIndividualServices(
            [...rooms, ...others].map((it) => ({
              keyID: Number(it.keyId || it.keyID || 0),
              roomID: Number(it.roomId || it.roomID || 0),
              childId: Number(it.childId),
              quantity: Number(it.quantity || 1),
              rate: Number(it.rate || 0),
              typeId: Number(it.typeId || 0),
              parentId: Number(it.parentId || 0),
            })),
          );

          // Keep subCustomers empty for venue bookings (this is key!)
          setSubCustomers([]);
          setActiveGuestIndex(null);
        }

        // Capture original venue details for optimization (company booking)
        const allCurrentServices = [
          ...(selectedRooms || []),
          ...(selectedChildren || []),
        ];
        const currentVenueServices = allCurrentServices.filter(
          (service) => !isAccommodation(service),
        );

        setOriginalVenueDetails({
          selectedDate: orderDate || null,
          venueServices: currentVenueServices.map((s) => ({
            childId: s.childId,
            parentId: s.parentId,
            typeId: s.typeId,
          })),
          isEdit: true,
          hydrationComplete: true,
        });

        console.log("📊 Captured original venue details for company booking:", {
          selectedDate: orderDate || null,
          venueServicesCount: currentVenueServices.length,
          hasSubCustomers: hasSubCustomerData,
        });
      }
    } catch (e) {
      // swallow hydration errors but keep UI usable
      console.error("Failed to hydrate edit view", e);
    }
  }, [data, paymentData]);

  // 🔧 NEW: Update selectedCustomer with credit details in edit mode when customerApi loads
  useEffect(() => {
    // Only run in edit mode when account payment is detected and customer API has data
    if (!data || !isOnAccount || !customerApi.isSuccess) return;

    const customerData = customerApi.data?.data || customerApi.data || [];
    if (!Array.isArray(customerData) || customerData.length === 0) return;

    const customerID = data?.customerID || data?.customerId;
    if (!customerID) return;

    // Find the matching customer with credit details
    const matchingCustomer = customerData.find(
      (customer) => Number(customer.customerID) === Number(customerID),
    );

    if (matchingCustomer) {
      console.log(
        "🔍 Setting customer with credit details from API:",
        matchingCustomer,
      );
      setSelectedCustomer({
        customerID: matchingCustomer.customerID,
        customerName: matchingCustomer.customerName,
        phoneNumber: matchingCustomer.phoneNumber,
        email: matchingCustomer.email,
        // Include credit details
        limit: matchingCustomer.limit,
        currentLimit: matchingCustomer.currentLimit,
        creditAccStatus: matchingCustomer.creditAccStatus,
        payment_types: matchingCustomer.payment_types,
        payment_Terms: matchingCustomer.payment_Terms,
      });
    }
  }, [data, isOnAccount, customerApi.isSuccess, customerApi.data]);

  // 🔧 NEW: Fetch company credit details when editing company bookings
  useEffect(() => {
    // Only run in edit mode when account payment is detected and we have company info
    if (!data || !isOnAccount || customerType !== "company") return;

    const companyID =
      companyForm.companyID || data?.companyID || data?.companyId;
    const companyName = companyForm.companyName || data?.companyName;

    // Only fetch if we have company info but missing credit details
    if (companyID && companyName && !companyForm.creditLimit) {
      console.log("🏢 Fetching company credit details for edit mode:", {
        companyID,
        companyName,
      });

      // Use the existing getCompany function
      getCompany({
        companyID: companyID,
        outletID: user.outletID,
      })
        .then((companyData) => {
          if (
            companyData &&
            Array.isArray(companyData) &&
            companyData.length > 0
          ) {
            const company = companyData[0]; // getCompany returns an array

            console.log("✅ Company credit details fetched:", {
              companyID: company.companyID,
              creditLimit: company.limit || company.creditLimit,
              currentLimit: company.currentLimit || company.availableLimit,
              creditAccStatus: company.creditAccStatus,
            });

            setCompanyForm((prev) => ({
              ...prev,
              creditLimit: company.limit || company.creditLimit || 0,
              currentLimit: company.currentLimit || company.availableLimit || 0,
              creditAccStatus: company.creditAccStatus || "",
            }));
          }
        })
        .catch((error) => {
          console.warn("⚠️ Failed to fetch company credit details:", error);
        });
    }
  }, [
    data,
    isOnAccount,
    customerType,
    companyForm.companyID,
    companyForm.companyName,
    companyForm.creditLimit,
    user.outletID,
  ]);

  // Update quantity for a selected room (remove if <=0)
  const updateRoomQuantity = (childId, newQty) => {
    setSelectedRooms((prev) =>
      prev.flatMap((room) => {
        if (Number(room.childId) !== Number(childId)) return [room];
        if (newQty <= 0) return []; // remove room if quantity drops to 0
        return [{ ...room, quantity: Number(newQty) }];
      }),
    );
  };

  // Helper: build combined service details (rooms + selected children)
  const buildServiceDetails = () => {
    // FIXED: Helper function to determine correct service ID for backend
    const getCorrectServiceId = (item) => {
      const isEditing = Boolean(data && (data.orderID || data.orderId));

      if (isEditing) {
        // For existing items, prioritize the original serviceId from API
        const existingServiceId = Number(
          item.originalServiceId || item.serviceId || item.serviceID || 0,
        );

        if (existingServiceId > 0) {
          console.log(
            `[EDIT] Preserving existing serviceId ${existingServiceId} for item:`,
            item.typeName,
          );
          return existingServiceId;
        }

        // If no existing serviceId found but has keyId, this is still an existing item
        const hasKeyId = Number(item.keyId || item.keyID || 0) > 0;
        if (hasKeyId) {
          console.warn(
            `[EDIT] Existing item with keyId but no serviceId found, using serviceTypeId as fallback:`,
            item,
          );
          // Use serviceTypeId as fallback for existing items without serviceId
          const serviceTypeId = Number(item.serviceTypeId || item.typeId || 0);
          if (serviceTypeId > 0) {
            return serviceTypeId;
          }
        }
      }

      // For new items or when no existing serviceId found, use childId/typeId (catalog ID)
      const catalogId = Number(
        item.childId || item.typeId || item.serviceTypeId || 0,
      );

      if (catalogId === 0) {
        console.error("Warning: All service ID options are 0 for item:", item);
      }

      const logType = isEditing ? "[EDIT-NEW]" : "[CREATE]";
      console.log(
        `${logType} Using catalog ID ${catalogId} for item:`,
        item.typeName,
      );
      return catalogId;
    };

    const nightsVal = (() => {
      const n = calculateNights(checkInDate, checkOutDate);
      return n > 0 ? n : 1;
    })();
    // Safety net: ensure unique lines per childId before mapping
    const dedupeByChildId = (arr) => {
      const map = new Map();
      (arr || []).forEach((item) => {
        const key = Number(item.childId);
        map.set(key, { ...item, childId: key });
      });
      return Array.from(map.values());
    };
    const uniqueRooms = dedupeByChildId(selectedRooms);
    const uniqueChildren = dedupeByChildId(selectedChildren);
    const roomDetails = uniqueRooms.map((room) => {
      const acc = isAccommodation(room) || true; // selectedRooms are accommodations
      const detailPrice = acc
        ? room.rate * room.quantity * nightsVal
        : room.rate * room.quantity;

      // FIXED: Preserve original roomID and parentID from API
      const roomID = Number(room.roomId ?? room.roomID ?? 0);
      const parentID = Number(room.parentId ?? room.parentID ?? 0);
      const serviceID = getCorrectServiceId(room);

      console.log(
        `Building room detail - serviceID: ${serviceID}, roomID: ${roomID}, parentID: ${parentID}`,
        room,
      );

      return {
        serviceID,
        typeID: Number(room.typeId ?? room.typeID ?? 0),
        serviceQuantity: Number(room.quantity ?? 1),
        serviceRate: Number(room.rate ?? 0),
        detailPrice: Number(detailPrice),
        parentID,
        nights: acc ? nightsVal : 0,
        keyID: Number(room.keyId ?? room.keyID ?? 0),
        orderDetailPrimaryID: Number(room.orderDetailPrimaryID ?? 0),
        roomID,
      };
    });
    const childDetails = uniqueChildren.map((c) => {
      const acc = isAccommodation(c);
      const detailPrice = acc
        ? c.rate * c.quantity * nightsVal
        : c.rate * c.quantity;

      // FIXED: Preserve original roomID and parentID from API
      const roomID = Number(c.roomId ?? c.roomID ?? 0);
      const parentID = Number(c.parentId ?? c.parentID ?? 0);
      const serviceID = getCorrectServiceId(c);

      console.log(
        `Building child detail - serviceID: ${serviceID}, roomID: ${roomID}, parentID: ${parentID}`,
        c,
      );

      return {
        serviceID,
        typeID: Number(c.typeId ?? c.typeID ?? 0),
        serviceQuantity: Number(c.quantity ?? 1),
        serviceRate: Number(c.rate ?? 0),
        detailPrice: Number(detailPrice),
        parentID,
        nights: acc ? nightsVal : 0,
        keyID: Number(c.keyId ?? c.keyID ?? 0),
        orderDetailPrimaryID: Number(c.orderDetailPrimaryID ?? 0),
        roomID,
      };
    });
    const guestDetails =
      customerType === "company"
        ? subCustomers.flatMap((g, guestIndex) =>
            (g.services || [])
              .filter((s) => {
                // CRITICAL FIX: Filter out phantom/invalid services
                const serviceRate = Number(s.rate || 0);
                const serviceId = Number(s.childId || s.typeId || 0);

                // Exclude services with zero rate (phantom services)
                if (serviceRate <= 0) {
                  console.log(`Excluding phantom service with zero rate:`, s);
                  return false;
                }

                // Exclude services with invalid ID
                if (serviceId <= 0) {
                  console.log(`Excluding service with invalid ID:`, s);
                  return false;
                }

                // Only include services that have a proper name/type
                const hasName =
                  (
                    s.name ||
                    s.typeName ||
                    s.productServiceName ||
                    s.rawName ||
                    ""
                  ).trim().length > 0;
                if (!hasName) {
                  console.log(`Excluding service with no name:`, s);
                  return false;
                }

                console.log(`Including valid service:`, {
                  serviceId,
                  rate: serviceRate,
                  name: s.name || s.typeName || s.productServiceName,
                  guestName: g.customerName,
                });

                return true;
              })
              .map((s) => {
                console.log(
                  `Building service for guest ${guestIndex} (${g.customerName}):`,
                  s,
                );
                const nights = calculateNights(
                  g.customerCheckIn,
                  g.customerCheckOut,
                );
                const acc = isAccommodation(s);
                const detailPrice = acc
                  ? s.rate * s.quantity * nights
                  : s.rate * s.quantity;
                return {
                  serviceID: getCorrectServiceId(s),
                  typeID: s.typeId,
                  serviceQuantity: s.quantity,
                  serviceRate: s.rate,
                  detailPrice,
                  parentID: s.parentId || 0,
                  guestName: g.customerName,
                  // Tag this line with the guest's own SubCustomerID so the
                  // backend can persist which guest each product belongs to
                  // instead of collapsing all guests' services together.
                  subCustomerId: Number(g.customerID || 0),
                  nights: acc ? nights : 0,
                  keyID: Number(s.keyId ?? s.keyID ?? 0),
                  orderDetailPrimaryID: Number(s.orderDetailPrimaryID ?? 0),
                  roomID: Number(s.roomId ?? s.roomID ?? 0),
                };
              }),
          )
        : [];
    const combined = [...roomDetails, ...childDetails, ...guestDetails];
    const isEditing = Boolean(data && (data.orderID || data.orderId));
    if (!isEditing) return combined;

    // EDIT MODE (INDIVIDUAL): now send FULL list (rooms + services) with roomID so backend can diff and update.
    if (customerType === "individual")
      return combined.filter((l) => !l.guestName);

    // COMPANY EDIT MODE: Handle both venue bookings and accommodation bookings
    if (customerType === "company") {
      // For company venue bookings (no guest cards), include main-level services AND guest services
      if (subCustomers.length === 0) {
        console.log(
          "Company venue booking edit: including main-level + guest services",
        );
        return combined; // Include both main-level services (roomDetails + childDetails) and any guest services
      }

      // For company accommodation bookings (with guest cards), only include guest services
      console.log(
        "Company accommodation booking edit: including guest services only",
      );
      return combined.filter((i) => i.guestName);
    }

    return combined;
  };

  // Helpers: normalize and detect HOLD status (backend may send variations like "On Hold", "on hold", "Account is on hold - contact Credit Manager")
  const normalizeStatus = (val) =>
    val == null ? "" : String(val).toLowerCase().trim();

  // Derive raw status from whichever object currently has data
  const rawHoldStatus = (() => {
    if (customerType === "company") {
      return (
        companyForm?.creditAccStatus ||
        companyForm?.accountStatus ||
        companyForm?.status ||
        ""
      );
    }
    return (
      customerForm?.creditAccStatus ||
      customerForm?.accountStatus ||
      customerForm?.status ||
      selectedCustomer?.creditAccStatus ||
      ""
    );
  })();

  const isAccountOnHold =
    /on\s*hold/.test(normalizeStatus(rawHoldStatus)) ||
    normalizeStatus(rawHoldStatus).includes("hold");

  // Clear current selection so user can pick a different customer/company when on hold
  const clearHoldSelection = () => {
    if (customerType === "company") {
      setCompanyForm({
        companyName: "",
        companyAddress: "",
        companyContact: "",
        companyEmail: "",
        companyID: 0,
        creditAccStatus: "",
        limit: 0,
        currentLimit: 0,
      });
      setSubCustomers([]);
    } else {
      setCustomerForm((prev) => ({
        ...prev,
        customerName: "",
        email: "",
        phoneNumber: "",
        creditAccStatus: "",
        isCustomerSelected: false,
      }));
      setSelectedCustomer(null);
    }
    // Clear any staged booking/service data
    setSelectedRooms([]);
    setSelectedChildren([]);
    setNumberOfGuests(1); // Reset guest count
    // setGuests removed: no guests state maintained separately (subCustomers covers company guests)
  };

  return (
    <div>
      {/* Full-screen booking submission overlay */}
      {isSubmitting && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(4, 63, 110, 0.85)",
            backdropFilter: "blur(4px)",
          }}
        >
          <style>
            {`
              .booking-loader {
                height: 60px;
                aspect-ratio: 2;
                position: relative;
              }
              .booking-loader:before {
                content: "";
                position: absolute;
                inset: 100% 75% -3px 0;
                background: #fff;
                animation: bl-0 1s linear infinite alternate;
              }
              .booking-loader:after {
                content: "";
                position: absolute;
                inset: auto 42.5% 0;
                aspect-ratio: 1;
                border-radius: 50%;
                background: #d4ac0d;
                animation:
                  bl-1 1s cubic-bezier(0,700,1,700) infinite alternate,
                  bl-2 1s linear infinite alternate;
              }
              @keyframes bl-0 {
                0%,30% { translate: 0%   -2px; rotate: 20deg }
                70%,to { translate: 300% -2px; rotate:-20deg }
              }
              @keyframes bl-1 {
                0%,2%  { bottom: 0% }
                98%,to { bottom: .1% }
              }
              @keyframes bl-2 {
                0%  { translate: -220% }
                to  { translate:  220% }
              }
            `}
          </style>
          {/* Seesaw loader */}
          <div className="booking-loader" style={{ marginBottom: 28 }} />
          {/* Text */}
          <p
            style={{
              color: "#fff",
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: 0.5,
              margin: 0,
            }}
          >
            Processing your booking
          </p>
          <p
            style={{
              color: "rgba(255,255,255,0.6)",
              fontSize: 13,
              marginTop: 14,
            }}
          >
            Please wait — do not close or refresh
          </p>
        </div>
      )}
      <div className="row align-items-center justify-content-between mb-2">
        <div className="col">
          <h5 className="fw-500 text-white">Add Booking</h5>
        </div>
      </div>

      <div className="row position-relative">
        {isAccountOnHold && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center"
            style={{
              background: "rgba(255,255,255,0.85)",
              zIndex: 50,
              backdropFilter: "blur(2px)",
            }}
          >
            <div
              className="bg-white border rounded shadow-sm p-4 text-center"
              style={{ maxWidth: 500 }}
            >
              <CiWarning size={46} className="text-warning mb-2" />
              <h5 className="mb-2">Account On Hold</h5>
              <p className="small mb-3">
                This account is currently on hold. You cannot proceed with a
                booking for it. Search and select another customer/company to
                continue.
              </p>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={clearHoldSelection}
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}
        {/* Left side - Booking Form */}
        <div className="col-lg-6 col-12">
          <div className="card mb-4">
            <div className="card-header p-4">
              <h5 className="mb-3">Booking Details</h5>

              {/* Account Hold Banner */}
              {isAccountOnHold && (
                <div
                  className="alert alert-warning d-flex align-items-center mb-3"
                  role="alert"
                >
                  <CiWarning className="me-2" size={24} />
                  <strong>Account on Hold:</strong>&nbsp;This account is
                  currently on hold. You can search for other customers, but
                  booking operations are disabled.
                </div>
              )}

              {/* Customer Type Selection */}
              <div className="mb-4">
                <label className="form-label">Booking Type</label>
                <div className="d-flex gap-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="customerType"
                      id="individual"
                      checked={customerType === "individual"}
                      onChange={() => handleCustomerTypeChange("individual")}
                    />
                    <label className="form-check-label" htmlFor="individual">
                      Individual/Family
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="customerType"
                      id="company"
                      checked={customerType === "company"}
                      onChange={() => handleCustomerTypeChange("company")}
                    />
                    <label className="form-check-label" htmlFor="company">
                      Company Booking
                    </label>
                  </div>
                </div>
              </div>

              {/* Individual Customer Form */}
              {customerType === "individual" && (
                <div className="mb-4">
                  <h6 className="mb-3">Customer Details</h6>

                  <div className="mb-3">
                    <label className="form-label">Salutation</label>
                    <select
                      className="form-control"
                      name="salutation"
                      value={customerForm.salutation}
                      onChange={handleCustomerFormChange}
                    >
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Ms">Ms</option>
                      <option value="Dr">Dr</option>
                    </select>
                  </div>

                  <div className="mb-3 position-relative">
                    <label className="form-label">
                      Customer Name<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter Customer Name"
                      name="customerName"
                      value={customerForm.customerName}
                      onChange={handleCustomerFormChange}
                      required
                    />

                    {/* Customer search results dropdown */}
                    {showSuggestions &&
                      customerApi.isSuccess &&
                      customerApi.data.data.length > 0 && (
                        <ul className="list-group position-absolute w-100 z-index-3">
                          {customerApi.data.data.map((customer) => (
                            <li
                              key={customer.customerID}
                              className="list-group-item customhover2 cursor-pointer"
                              onClick={() => handleCustomerSelect(customer)}
                            >
                              <span className="fw-bold d-block">
                                {customer.customerName}
                              </span>
                              <small
                                style={{ fontSize: "12px" }}
                                className="d-block text-warning"
                              >
                                {customer.phoneNumber}
                              </small>
                              <small
                                style={{ fontSize: "12px" }}
                                className="d-block text-danger"
                              >
                                {customer.email}
                              </small>
                              <small
                                style={{ fontSize: "12px" }}
                                className="d-block text-info"
                              >
                                {customer.address}
                              </small>
                            </li>
                          ))}
                        </ul>
                      )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Email
                      {!customerForm.isCustomerSelected && (
                        <span className="text-info">
                          {" "}
                          (Recommended for new customers)
                        </span>
                      )}
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="Email Address"
                      name="email"
                      value={customerForm.email}
                      onChange={handleCustomerFormChange}
                      disabled={isAccountOnHold}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Contact Number<span className="text-danger">*</span>
                    </label>
                    <div className="row g-2">
                      <div className="col-4">
                        <select
                          className="form-control"
                          name="countryCode"
                          value={customerForm.countryCode}
                          onChange={handleCustomerFormChange}
                          disabled={isAccountOnHold}
                        >
                          {allCountries.map((country) => (
                            <option
                              key={country.iso2}
                              value={`+${country.dialCode}`}
                            >
                              +{country.dialCode}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-8">
                        <input
                          type="tel"
                          className="form-control"
                          placeholder="Contact Number"
                          name="phoneNumber"
                          value={customerForm.phoneNumber}
                          onChange={handleCustomerFormChange}
                          required
                          disabled={isAccountOnHold}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Bill to Account Section */}
              {((customerType === "individual" &&
                customerForm.isCustomerSelected &&
                selectedCustomer?.customerID) ||
                (customerType === "company" &&
                  companyForm.isCompanySelected &&
                  companyForm.companyID)) && (
                <div className="mb-4">
                  <h6 className="mb-3">Payment Options</h6>

                  {/* Credit Account Information Display */}
                  {(() => {
                    // Get the ACTUAL CREDIT LIMIT (total facility)
                    const creditLimit =
                      customerType === "company"
                        ? (companyForm?.creditLimit ?? companyForm?.limit ?? 0)
                        : (selectedCustomer?.creditLimit ??
                          selectedCustomer?.limit ??
                          0);

                    const num = Number(creditLimit);
                    const isValidLimit = Number.isFinite(num) && num > 0;

                    const accountStatus =
                      customerType === "company"
                        ? companyForm?.creditAccStatus
                        : selectedCustomer?.creditAccStatus;

                    const isOnHold =
                      accountStatus &&
                      accountStatus.toString().toLowerCase().trim() ===
                        "on hold";

                    if (isValidLimit && !isOnHold) {
                      // Get the AVAILABLE credit (what they can still spend)
                      const current =
                        customerType === "company"
                          ? Number(companyForm?.currentLimit ?? 0)
                          : Number(selectedCustomer?.currentLimit ?? 0);

                      return (
                        <div className="alert alert-info mb-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>Credit Account Available</strong>
                              <br />
                              <small className="text-success">
                                Credit limit: R {num.toFixed(2)} | Available: R{" "}
                                {current.toFixed(2)}
                              </small>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return null;
                  })()}

                  {/* Bill to Account Toggle */}
                  <div className="form-check mb-3">
                    <input
                      id="billToAccount"
                      type="checkbox"
                      className="form-check-input"
                      checked={isOnAccount}
                      disabled={(() => {
                        // 🔧 NEW: Disable if in edit mode and payment is already made to account
                        const isEditModeWithAccountPayment =
                          data && // Edit mode (has booking data)
                          paymentData && // Has payment data
                          (Number(paymentData.paymentType) === 4 ||
                            Number(paymentData.paymentType) === 10001); // Check the paymentType directly

                        if (isEditModeWithAccountPayment) {
                          return true; // Disable - payment already processed
                        }

                        const accountStatus =
                          customerType === "company"
                            ? companyForm?.creditAccStatus
                            : selectedCustomer?.creditAccStatus;

                        if (
                          accountStatus &&
                          accountStatus.toString().toLowerCase().trim() ===
                            "on hold"
                        ) {
                          return true; // Disable if on hold
                        }

                        // Check actual credit limit exists
                        const creditLimit =
                          customerType === "company"
                            ? (companyForm?.creditLimit ??
                              companyForm?.limit ??
                              0)
                            : (selectedCustomer?.creditLimit ??
                              selectedCustomer?.limit ??
                              0);

                        // Check available credit for validation
                        const availableCredit =
                          customerType === "company"
                            ? (companyForm?.currentLimit ?? 0)
                            : (selectedCustomer?.currentLimit ?? 0);

                        const totalLimit = Number(creditLimit);
                        const available = Number(availableCredit);

                        return (
                          !Number.isFinite(totalLimit) ||
                          totalLimit <= 0 ||
                          !Number.isFinite(available) ||
                          available <= 0
                        );
                      })()}
                      onChange={(e) => {
                        const desired = e.target.checked;

                        // Check if account is on hold
                        const accountStatus =
                          customerType === "company"
                            ? companyForm?.creditAccStatus
                            : selectedCustomer?.creditAccStatus;

                        if (
                          accountStatus &&
                          accountStatus.toString().toLowerCase().trim() ===
                            "on hold"
                        ) {
                          toast.error(
                            "Cannot bill to account - account is on hold",
                          );
                          return;
                        }

                        // Check available credit (not just credit limit)
                        const availableCredit =
                          customerType === "company"
                            ? (companyForm?.currentLimit ?? 0)
                            : (selectedCustomer?.currentLimit ?? 0);

                        const available = Number(availableCredit);
                        if (!Number.isFinite(available) || available <= 0) {
                          toast.error("No available credit limit");
                          return;
                        }

                        setIsOnAccount(desired);
                        if (!desired) {
                          setCreateQuotation(false);
                        }
                      }}
                    />
                    <label className="form-check-label" htmlFor="billToAccount">
                      Bill to Account
                      {(() => {
                        // 🔧 NEW: Show edit mode status
                        const isEditModeWithAccountPayment =
                          data && // Edit mode (has booking data)
                          paymentData && // Has payment data
                          (Number(paymentData.paymentType) === 4 ||
                            Number(paymentData.paymentType) === 10001); // Check the paymentType directly

                        if (isEditModeWithAccountPayment) {
                          return (
                            <span className="text-info ms-2">
                              (Already processed - cannot change)
                            </span>
                          );
                        }

                        const accountStatus =
                          customerType === "company"
                            ? companyForm?.creditAccStatus
                            : selectedCustomer?.creditAccStatus;

                        const isOnHold =
                          accountStatus &&
                          accountStatus.toString().toLowerCase().trim() ===
                            "on hold";

                        if (isOnHold) {
                          return (
                            <span className="text-danger ms-2">
                              (Account on Hold)
                            </span>
                          );
                        }

                        const availableCredit =
                          customerType === "company"
                            ? (companyForm?.currentLimit ?? 0)
                            : (selectedCustomer?.currentLimit ?? 0);

                        const available = Number(availableCredit);
                        if (!Number.isFinite(available) || available <= 0) {
                          return (
                            <span className="text-muted ms-2">
                              (No Available Credit)
                            </span>
                          );
                        }

                        return null;
                      })()}
                    </label>
                  </div>

                  {/* Create Quotation Toggle - Show when Bill to Account is selected and NOT in edit mode */}
                  {isOnAccount && !data && (
                    <div className="form-check mt-2">
                      <input
                        id="createQuotation"
                        type="checkbox"
                        className="form-check-input"
                        checked={createQuotation}
                        onChange={(e) => setCreateQuotation(e.target.checked)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="createQuotation"
                      >
                        Create Quotation
                        <small className="text-muted ms-2">
                          (Send quotation instead of immediate booking)
                        </small>
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* Individual Booking Dates */}
              {customerType === "individual" && (
                <div className="mb-4">
                  <h6 className="mb-3">
                    {getDateFieldType() === "dual"
                      ? "Booking Dates"
                      : "Event Date"}
                  </h6>

                  {/* 🔒 COOL ALERT: Show lock message for confirmed individual venue bookings */}
                  {isConfirmedVenueBooking() && (
                    <div className="alert alert-warning mb-3">
                      <i className="fas fa-lock me-2"></i>
                      <strong>Booking Confirmed:</strong> This venue booking is
                      confirmed and cannot be modified. The event date and venue
                      details are locked to prevent accidental changes.
                    </div>
                  )}

                  {/* Info message when no services selected */}
                  {selectedRooms.length === 0 &&
                    selectedChildren.length === 0 && (
                      <div className="alert alert-info mb-3">
                        <i className="fa fa-info-circle me-2"></i>
                        <strong>Date Selection Guide:</strong>
                        <ul className="mb-0 mt-2" style={{ fontSize: "14px" }}>
                          <li>
                            <strong>Accommodation bookings:</strong> After
                            selecting accommodation services (rooms), you'll see
                            Check-in and Check-out date fields
                          </li>
                          <li>
                            <strong>Venue/Event bookings:</strong> For venues
                            and other services, use the Event Date field for
                            single-day bookings
                          </li>
                        </ul>
                      </div>
                    )}

                  <div className="row">
                    {getDateFieldType() === "dual" ? (
                      <>
                        {/* Dual date fields for accommodation services */}
                        <div className="col-md-6">
                          <label className="form-label">
                            Check-in Date<span className="text-danger">*</span>
                          </label>
                          <DatePicker
                            className="form-control"
                            value={checkInDate ? moment(checkInDate) : null}
                            onChange={(date, dateString) =>
                              setCheckInDate(dateString)
                            }
                            format="YYYY-MM-DD"
                            required
                            disabled={isAccountOnHold}
                          />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">
                            Check-out Date<span className="text-danger">*</span>
                          </label>
                          <DatePicker
                            className="form-control"
                            value={checkOutDate ? moment(checkOutDate) : null}
                            onChange={(date, dateString) =>
                              setCheckOutDate(dateString)
                            }
                            format="YYYY-MM-DD"
                            required
                            disabled={isAccountOnHold}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Single date field for venue/other services */}
                        <div className="col-md-6">
                          <label className="form-label">
                            Event Date<span className="text-danger">*</span>
                            {isConfirmedVenueBooking() && (
                              <span className="ms-2 badge bg-warning text-dark">
                                <i className="fas fa-lock me-1"></i>
                                LOCKED
                              </span>
                            )}
                          </label>
                          <DatePicker
                            className="form-control"
                            value={selectedDate ? moment(selectedDate) : null}
                            onChange={(date, dateString) => {
                              // 🔒 COOL BLOCK: Prevent date changes for confirmed bookings
                              if (isConfirmedVenueBooking()) {
                                toast.error(
                                  "Cannot modify date - venue booking is confirmed!",
                                );
                                return;
                              }

                              setSelectedDate(dateString);
                              // Also set checkInDate and checkOutDate for backend consistency
                              setCheckInDate(dateString);
                              setCheckOutDate(dateString);
                            }}
                            format="YYYY-MM-DD"
                            required
                            disabled={
                              isAccountOnHold || isConfirmedVenueBooking()
                            } // 🔒 Disable for confirmed bookings
                            style={{
                              // 🎨 Visual indicator for locked state
                              backgroundColor: isConfirmedVenueBooking()
                                ? "#f8f9fa"
                                : "white",
                              cursor: isConfirmedVenueBooking()
                                ? "not-allowed"
                                : "pointer",
                            }}
                          />
                        </div>
                        <div className="col-md-6">
                          <div
                            className="alert alert-light border mt-2 mb-0"
                            style={{ fontSize: "13px" }}
                          >
                            <i className="fa fa-lightbulb text-warning me-2"></i>
                            <strong>Note:</strong> This is for venue/event
                            bookings. If you select{" "}
                            <strong>accommodation services</strong>, the date
                            fields will change to Check-in and Check-out dates.
                          </div>
                        </div>
                      </>
                    )}

                    {/* Availability Status - only show for accommodation services */}
                    {getDateFieldType() === "dual" && (
                      <>
                        {checkingAvailability && (
                          <div className="mt-2 text-info">
                            <i className="fa fa-spinner fa-spin me-2"></i>
                            Checking availability...
                          </div>
                        )}
                        {roomsAvailable === true && (
                          <div className="mt-2 text-success">
                            <i className="fa fa-check me-2"></i>
                            Rooms available for selected dates
                          </div>
                        )}
                        {roomsAvailable === false && (
                          <div className="mt-2 text-danger">
                            <i className="fa fa-times me-2"></i>
                            No rooms available for selected dates
                          </div>
                        )}
                      </>
                    )}

                    {/* Venue Availability Status for single date scenarios */}
                    {getDateFieldType() === "single" &&
                      !hasAccommodationServices() && (
                        <>
                          {checkingVenueAvailability && (
                            <div className="mt-2 text-info">
                              <i className="fa fa-spinner fa-spin me-2"></i>
                              Checking venue availability...
                            </div>
                          )}
                          {venuesAvailable === true && (
                            <div className="mt-2 text-success">
                              <i className="fa fa-check me-2"></i>
                              Venue available for selected date
                            </div>
                          )}
                          {venuesAvailable === false && (
                            <div className="mt-2 text-danger">
                              <i className="fa fa-times me-2"></i>
                              Venue not available for selected date
                            </div>
                          )}
                        </>
                      )}

                    {/* Number of Guests - Show for accommodation OR venue services */}
                    {(hasParentId218Services() || hasVenueServices()) && (
                      <div className="row mt-4">
                        <div className="col-md-6">
                          <label className="form-label">
                            Number of Guests
                            <span className="text-danger">*</span>
                          </label>
                          <div className="d-flex align-items-center gap-2">
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={() =>
                                setNumberOfGuests((prev) =>
                                  Math.max(1, prev - 1),
                                )
                              }
                              disabled={numberOfGuests <= 1 || isAccountOnHold}
                            >
                              <i>-</i>
                            </button>
                            <input
                              type="number"
                              className="form-control text-center"
                              style={{ maxWidth: "80px", marginTop: "-14px" }}
                              value={numberOfGuests}
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 1;
                                setNumberOfGuests(
                                  Math.max(1, Math.min(600, value)),
                                );
                              }}
                              min="1"
                              max="600"
                              disabled={isAccountOnHold}
                            />
                            <button
                              type="button"
                              className="btn btn-outline-primary"
                              onClick={() =>
                                setNumberOfGuests((prev) =>
                                  Math.min(600, prev + 1),
                                )
                              }
                              disabled={
                                numberOfGuests >= 600 || isAccountOnHold
                              }
                            >
                              <i> +</i>
                            </button>
                          </div>
                          <small className="text-muted mt-1 d-block">
                            <IoIosPeople size={20} />
                            Total guests for{" "}
                            {hasParentId218Services()
                              ? "accommodation"
                              : "venue"}{" "}
                            booking
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Company Booking Form */}
              {customerType === "company" && (
                <div className="mb-4">
                  <h6 className="mb-3">Company Details</h6>

                  <div className="mb-3 position-relative">
                    <label className="form-label">
                      Company Name<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter Company Name"
                      name="companyName"
                      value={companyForm.companyName}
                      onChange={handleCompanyFormChange}
                      required
                    />

                    {/* Company suggestions */}
                    {companySuggestions.length > 0 && (
                      <ul className="list-group position-absolute w-100 z-index-3">
                        {companySuggestions.map((company) => (
                          <li
                            key={company.companyID}
                            className="list-group-item customhover2 cursor-pointer"
                            onClick={() => handleCompanySelect(company)}
                          >
                            <span className="fw-bold d-block">
                              {company.companyName}
                            </span>
                            <small
                              style={{ fontSize: "12px" }}
                              className="d-block text-info"
                            >
                              {company.address}
                            </small>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Company Address</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Company Address"
                      name="companyAddress"
                      value={companyForm.companyAddress}
                      onChange={handleCompanyFormChange}
                      disabled={isAccountOnHold}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Company Contact<span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      className="form-control"
                      placeholder="Company Contact Number"
                      name="companyContact"
                      value={companyForm.companyContact || ""}
                      onChange={handleCompanyFormChange}
                      disabled={isAccountOnHold}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Company Email</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="Company Email"
                      name="companyEmail"
                      value={companyForm.companyEmail || ""}
                      onChange={handleCompanyFormChange}
                      disabled={isAccountOnHold}
                    />
                  </div>

                  {/* Sub-customers section - Always show Add Guest button */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        Employees/Guests
                        {hasAccommodationServices() && (
                          <span className="text-danger">*</span>
                        )}
                      </h6>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={addSubCustomer}
                        disabled={isAccountOnHold}
                      >
                        Add Guest
                      </button>
                    </div>

                    {/* Show message when no guests added yet */}
                    {subCustomers.length === 0 && (
                      <div className="alert alert-light border">
                        <i className="fa fa-info-circle me-2"></i>
                        <strong>Getting Started:</strong>
                        <ul className="mb-0 mt-2">
                          <li>
                            For <strong>accommodation bookings</strong>: Click
                            "Add Guest" first, then select accommodation
                            services
                          </li>
                          <li>
                            For <strong>venue bookings</strong>: Select venue
                            services directly - no individual guests needed
                          </li>
                        </ul>
                      </div>
                    )}

                    {/* Show helpful message for multiple guests */}
                    {subCustomers.length > 5 && (
                      <div className="alert alert-info border-info">
                        <i className="fa fa-lightbulb me-2"></i>
                        <strong>Multiple Guests:</strong> You can add up to 600
                        guests! Only complete guest details with names and dates
                        will be included in the final booking.
                      </div>
                    )}

                    {/* Show guest cards when there are guests */}
                    {subCustomers.length > 0 && (
                      <div className="row">
                        {subCustomers.map((subCustomer, index) => (
                          <div key={index} className="col-12 mb-3">
                            <div
                              className="border p-3 rounded"
                              style={{ background: "#fff" }}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <small className="fw-bold text-secondary">
                                  Guest #{index + 1}
                                </small>
                                <button
                                  type="button"
                                  className="btn btn-xs btn-outline-danger"
                                  onClick={() => removeSubCustomer(index)}
                                  style={{
                                    fontSize: "11px",
                                    lineHeight: "1.2",
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                              <div className="row g-3">
                                <div className="col-md-6 col-12">
                                  <label
                                    className="form-label mb-1"
                                    style={{ fontSize: "12px" }}
                                  >
                                    Name<span className="text-danger">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Guest Name"
                                    value={subCustomer.customerName}
                                    onChange={(e) =>
                                      updateSubCustomer(
                                        index,
                                        "customerName",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </div>
                                <div className="col-md-6 col-12">
                                  <label
                                    className="form-label mb-1"
                                    style={{ fontSize: "12px" }}
                                  >
                                    Email
                                    <span className="text-muted">
                                      {" "}
                                      (Optional)
                                    </span>
                                  </label>
                                  <input
                                    type="email"
                                    className="form-control"
                                    placeholder="Email (Optional)"
                                    value={subCustomer.customerEmail || ""}
                                    onChange={(e) =>
                                      updateSubCustomer(
                                        index,
                                        "customerEmail",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </div>
                                <div className="col-md-6 col-12">
                                  <label
                                    className="form-label mb-1"
                                    style={{ fontSize: "12px" }}
                                  >
                                    Check-in
                                    <span className="text-danger">*</span>
                                  </label>
                                  <DatePicker
                                    className="form-control"
                                    value={
                                      subCustomer.customerCheckIn
                                        ? moment(subCustomer.customerCheckIn)
                                        : null
                                    }
                                    onChange={(date, dateString) =>
                                      updateSubCustomer(
                                        index,
                                        "customerCheckIn",
                                        dateString,
                                      )
                                    }
                                    format="YYYY-MM-DD"
                                    style={{ width: "100%" }}
                                    disabled={isAccountOnHold}
                                  />
                                </div>
                                <div className="col-md-6 col-12">
                                  <label
                                    className="form-label mb-1"
                                    style={{ fontSize: "12px" }}
                                  >
                                    Check-out
                                    <span className="text-danger">*</span>
                                  </label>
                                  <DatePicker
                                    className="form-control"
                                    value={
                                      subCustomer.customerCheckOut
                                        ? moment(subCustomer.customerCheckOut)
                                        : null
                                    }
                                    onChange={(date, dateString) =>
                                      updateSubCustomer(
                                        index,
                                        "customerCheckOut",
                                        dateString,
                                      )
                                    }
                                    format="YYYY-MM-DD"
                                    style={{ width: "100%" }}
                                    disabledDate={(current) => {
                                      if (!subCustomer.customerCheckIn)
                                        return false;
                                      return (
                                        current &&
                                        current <=
                                          moment(
                                            subCustomer.customerCheckIn,
                                          ).startOf("day")
                                      );
                                    }}
                                    disabled={isAccountOnHold}
                                  />
                                </div>
                                <div className="col-12">
                                  {subCustomer.availability === "checking" && (
                                    <small className="text-info">
                                      <i className="fa fa-spinner fa-spin me-1" />
                                      Checking availability...
                                    </small>
                                  )}
                                  {subCustomer.availability === "available" && (
                                    <small className="text-success">
                                      <i className="fa fa-check me-1" />
                                      Available
                                    </small>
                                  )}
                                  {subCustomer.availability ===
                                    "unavailable" && (
                                    <small className="text-danger">
                                      <i className="fa fa-times me-1" />
                                      Not available
                                    </small>
                                  )}
                                  {subCustomer.availability === "error" && (
                                    <small className="text-warning">
                                      <i className="fa fa-exclamation-triangle me-1" />
                                      Error checking availability
                                    </small>
                                  )}
                                </div>
                                <div className="col-12 mt-2">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <small className="text-muted">
                                      {subCustomer.services.length === 0
                                        ? "No services selected"
                                        : `${subCustomer.services.length} service(s)`}
                                    </small>
                                    <button
                                      type="button"
                                      className={`btn btn-xs ${
                                        activeGuestIndex === index
                                          ? "btn-primary"
                                          : "btn-outline-primary"
                                      }`}
                                      onClick={() => {
                                        if (
                                          activeGuestIndex !== null &&
                                          activeGuestIndex !== index
                                        ) {
                                          // Allow switching freely for now
                                        }
                                        if (activeGuestIndex !== index) {
                                          setSelectedProduct(null);
                                          setModalSelections({});
                                          setGuestActivationTick((t) => t + 1);
                                        }
                                        setActiveGuestIndex(index);
                                      }}
                                    >
                                      {activeGuestIndex === index
                                        ? "Active"
                                        : "Activate Card"}
                                    </button>
                                  </div>
                                  {subCustomer.services.length > 0 && (
                                    <div className="mt-1">
                                      {subCustomer.services
                                        .slice(0, 5)
                                        .map((s) => (
                                          <span
                                            key={s.childId}
                                            className="badge bg-secondary me-1 mb-1"
                                            style={{ fontSize: "10px" }}
                                          >
                                            {s.name || s.childId} x{s.quantity}
                                          </span>
                                        ))}
                                      {subCustomer.services.length > 5 && (
                                        <span
                                          className="badge bg-dark"
                                          style={{ fontSize: "10px" }}
                                        >
                                          +{subCustomer.services.length - 5}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                  <div className="text-end mt-1">
                                    <small className="fw-600">
                                      Total: R{" "}
                                      {guestTotal(subCustomer).toFixed(2)}
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Show message for venue bookings that guests are not required */}
                  {hasVenueServices() &&
                    !hasAccommodationServices() &&
                    subCustomers.length === 0 && (
                      <div className="mb-3">
                        <div className="alert alert-info">
                          <i className="fa fa-info-circle me-2"></i>
                          <strong>Venue Booking:</strong> Individual guests are
                          not required for venue bookings. The company is the
                          main guest for this reservation.
                        </div>
                      </div>
                    )}
                </div>
              )}

              {/* Company Booking Dates - Show based on service type */}
              {customerType === "company" && (
                <div className="mb-4">
                  <h6 className="mb-3">
                    {getDateFieldType() === "dual" && hasAccommodationServices()
                      ? "Accommodation Dates"
                      : "Event Date"}
                  </h6>

                  {/* 🔒 COOL ALERT: Show lock message for confirmed venue bookings */}
                  {isConfirmedVenueBooking() && (
                    <div className="alert alert-warning mb-3">
                      <i className="fas fa-lock me-2"></i>
                      <strong>Booking Confirmed:</strong> This venue booking is
                      confirmed and cannot be modified. The event date and venue
                      details are locked to prevent accidental changes.
                    </div>
                  )}

                  {/* Show venue booking fields only when no guests exist (venue mode only) */}
                  {subCustomers.length === 0 && (
                    <div className="row">
                      <div className="col-md-6">
                        <label className="form-label">
                          Event Date<span className="text-danger">*</span>
                          {isConfirmedVenueBooking() && (
                            <span className="ms-2 badge bg-warning text-dark">
                              <i className="fas fa-lock me-1"></i>
                              LOCKED
                            </span>
                          )}
                        </label>
                        <DatePicker
                          className="form-control"
                          value={selectedDate ? moment(selectedDate) : null}
                          onChange={(date, dateString) => {
                            // 🔒 COOL BLOCK: Prevent date changes for confirmed bookings
                            if (isConfirmedVenueBooking()) {
                              toast.error(
                                "Cannot modify date - venue booking is confirmed!",
                              );
                              return;
                            }

                            setSelectedDate(dateString);
                            // Also set checkInDate and checkOutDate for backend consistency
                            setCheckInDate(dateString);
                            setCheckOutDate(dateString);
                          }}
                          format="YYYY-MM-DD"
                          required
                          disabled={
                            isAccountOnHold || isConfirmedVenueBooking()
                          } // 🔒 Disable for confirmed bookings
                          style={{
                            // 🎨 Visual indicator for locked state
                            backgroundColor: isConfirmedVenueBooking()
                              ? "#f8f9fa"
                              : "white",
                            cursor: isConfirmedVenueBooking()
                              ? "not-allowed"
                              : "pointer",
                          }}
                        />
                      </div>
                      <div className="col-md-6">
                        <small
                          className={`mt-4 d-block ${
                            isConfirmedVenueBooking()
                              ? "text-warning"
                              : "text-muted"
                          }`}
                        >
                          {isConfirmedVenueBooking()
                            ? " Event date is locked for confirmed bookings"
                            : "Venues are typically booked for single-day events"}
                        </small>
                      </div>

                      {/* Number of Guests for company venue bookings */}
                      <div className="col-md-6 mt-3">
                        <label className="form-label">
                          Number of Guests
                          <span className="text-danger">*</span>
                        </label>
                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => {
                              setNumberOfGuests((prev) =>
                                Math.max(1, prev - 1),
                              );
                            }}
                            disabled={numberOfGuests <= 1 || isAccountOnHold}
                          >
                            <i>-</i>
                          </button>
                          <input
                            type="number"
                            className="form-control text-center"
                            style={{
                              maxWidth: "80px",
                              marginTop: "-14px",
                            }}
                            value={numberOfGuests}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 1;
                              setNumberOfGuests(
                                Math.max(1, Math.min(600, value)),
                              );
                            }}
                            min="1"
                            max="600"
                            disabled={isAccountOnHold}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => {
                              setNumberOfGuests((prev) =>
                                Math.min(600, prev + 1),
                              );
                            }}
                            disabled={numberOfGuests >= 600 || isAccountOnHold}
                          >
                            <i> +</i>
                          </button>
                        </div>
                        <small className="mt-1 d-block text-muted">
                          <IoIosPeople size={20} />
                          Total guests for venue booking
                        </small>
                      </div>

                      {/* Venue Availability Status - Only show if NOT confirmed */}
                      {!isConfirmedVenueBooking() && (
                        <>
                          {checkingVenueAvailability && (
                            <div className="mt-2 text-info">
                              <i className="fa fa-spinner fa-spin me-2"></i>
                              Checking venue availability...
                            </div>
                          )}
                          {venuesAvailable === true && (
                            <div className="mt-2 text-success">
                              <i className="fa fa-check me-2"></i>
                              Venue available for selected date
                            </div>
                          )}
                          {venuesAvailable === false && (
                            <div className="mt-2 text-danger">
                              <i className="fa fa-times me-2"></i>
                              Venue not available for selected date
                            </div>
                          )}
                        </>
                      )}

                      {/*  COOL STATUS: Show confirmation status for locked bookings */}
                      {isConfirmedVenueBooking() && (
                        <div className="mt-2">
                          <div className="alert alert-success mb-0">
                            <i className="fas fa-check-circle me-2"></i>
                            <strong>Venue Confirmed:</strong> This booking is
                            confirmed for {selectedDate}
                            {numberOfGuests > 1 &&
                              ` for ${numberOfGuests} guests`}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                // </div>
              )}
            </div>

            {/* Selected Services Summary */}
            <div className="card-body p-0">
              {(() => {
                const hasServices =
                  customerType === "company"
                    ? subCustomers.some(
                        (g) => g.services && g.services.length > 0,
                      ) ||
                      selectedRooms.length > 0 ||
                      selectedChildren.length > 0
                    : selectedRooms.length > 0 || selectedChildren.length > 0;
                if (!hasServices)
                  return (
                    <h6 className="px-4 text-center my-4 py-5">
                      No services selected yet.
                    </h6>
                  );
                let unified = [];
                if (customerType === "company") {
                  // If there are guest cards, use guest services
                  if (subCustomers.length > 0) {
                    unified = subCustomers.flatMap((g, guestIndex) =>
                      (g.services || []).map((s) => {
                        const acc = isAccommodation(s);
                        let nights = 0;
                        if (acc) {
                          // Primary: guest-specific dates
                          nights = calculateNights(
                            g.customerCheckIn,
                            g.customerCheckOut,
                          );
                          // Fallback: overall booking dates if guest dates not set / zero
                          if (!nights) {
                            const globalN = calculateNights(
                              checkInDate,
                              checkOutDate,
                            );
                            if (globalN) nights = globalN;
                          }
                          // Final fallback: treat as 1 night so user sees cost instead of '-'
                          if (!nights) nights = 1;
                        }
                        const displayName =
                          s.rawName ||
                          s.name ||
                          s.typeName ||
                          s.productServiceName ||
                          `Service ${s.childId}`;

                        // Debug logging for service names
                        if (process.env.NODE_ENV === "development") {
                          console.log(`Service for ${g.customerName}:`, {
                            rawName: s.rawName,
                            name: s.name,
                            typeName: s.typeName,
                            productServiceName: s.productServiceName,
                            finalDisplayName: displayName,
                          });
                        }

                        return {
                          id: s.childId,
                          name: displayName,
                          rate: s.rate,
                          quantity: s.quantity,
                          total: acc
                            ? s.rate * s.quantity * nights
                            : s.rate * s.quantity,
                          kind: acc ? "Room" : "Service",
                          guestName: g.customerName || "Guest",
                          // Stable index into subCustomers so quantity/remove
                          // actions target this exact guest, not any other
                          // guest that happens to share the same childId or
                          // display name (e.g. two guests booked into the
                          // same room type).
                          guestIndex,
                          // Store guest and nights info to avoid recalculation
                          guestCheckIn: g.customerCheckIn,
                          guestCheckOut: g.customerCheckOut,
                          nights: nights,
                        };
                      }),
                    );
                  } else {
                    // No guest cards - use selectedRooms/selectedChildren (venue mode)
                    const nightsVal = (() => {
                      const n = calculateNights(checkInDate, checkOutDate);
                      return n > 0 ? n : 1;
                    })();
                    unified = [
                      ...selectedRooms.map((r) => {
                        const acc = true;
                        return {
                          id: r.childId,
                          name:
                            r.typeName ||
                            r.productServiceName ||
                            r.name ||
                            `Room ${r.childId}`,
                          rate: r.rate,
                          quantity: r.quantity,
                          total: acc
                            ? r.rate * r.quantity * nightsVal
                            : r.rate * r.quantity,
                          kind: acc ? "Room" : "Service",
                        };
                      }),
                      ...selectedChildren.map((c) => {
                        const acc = isAccommodation(c);
                        const baseQty = c.quantity || 1;
                        return {
                          id: c.childId,
                          name:
                            c.typeName ||
                            c.productServiceName ||
                            c.name ||
                            `Service ${c.childId}`,
                          rate: c.rate,
                          quantity: baseQty,
                          total: acc
                            ? (c.rate || 0) * baseQty * nightsVal
                            : (c.rate || 0) * baseQty,
                          kind: acc ? "Room" : "Service",
                        };
                      }),
                    ];
                  }
                } else {
                  const nightsVal = (() => {
                    const n = calculateNights(checkInDate, checkOutDate);
                    return n > 0 ? n : 1;
                  })();
                  unified = [
                    ...selectedRooms.map((r) => {
                      const acc = true; // rooms list represent accommodation
                      return {
                        id: r.childId,
                        name:
                          r.typeName ||
                          r.productServiceName ||
                          r.name ||
                          `Room ${r.childId}`,
                        rate: r.rate,
                        quantity: r.quantity,
                        total: acc
                          ? r.rate * r.quantity * nightsVal
                          : r.rate * r.quantity,
                        kind: acc ? "Room" : "Service",
                      };
                    }),
                    ...selectedChildren.map((c) => {
                      const acc = isAccommodation(c);
                      const baseQty = c.quantity || 1;
                      return {
                        id: c.childId,
                        name:
                          c.typeName ||
                          c.productServiceName ||
                          c.name ||
                          `Service ${c.childId}`,
                        rate: c.rate,
                        quantity: baseQty,
                        total: acc
                          ? (c.rate || 0) * baseQty * nightsVal
                          : (c.rate || 0) * baseQty,
                        kind: acc ? "Room" : "Service",
                      };
                    }),
                  ];
                }
                return (
                  <>
                    <div className="table-responsive">
                      <table className="table mb-0">
                        <thead className="bg-light">
                          <tr>
                            {customerType === "company" && (
                              <th className="text-uppercase text-secondary text-xs opacity-7">
                                Guest
                              </th>
                            )}
                            <th className="text-uppercase text-secondary text-xs opacity-7">
                              Type
                            </th>
                            <th className="text-uppercase text-secondary text-xs opacity-7">
                              Name
                            </th>
                            <th className="text-uppercase text-secondary text-xs opacity-7">
                              Rate
                            </th>
                            {(customerType === "company" ||
                              customerType === "individual") && (
                              <th className="text-uppercase text-secondary text-xs opacity-7">
                                Nights
                              </th>
                            )}
                            <th className="text-uppercase text-secondary text-xs opacity-7">
                              Qty
                            </th>
                            <th className="text-uppercase text-secondary text-xs opacity-7">
                              Total
                            </th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {unified.map((row, i) => (
                            <tr key={row.id + "-" + i}>
                              {customerType === "company" && (
                                <td
                                  className="text-xs px-3"
                                  style={{
                                    maxWidth: 120,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {row.guestName}
                                </td>
                              )}
                              <td className="text-xs px-3 fw-600">
                                {row.kind === "Room" ? (
                                  <span className="badge bg-primary">Room</span>
                                ) : (
                                  <span className="badge bg-dark">Service</span>
                                )}
                              </td>
                              <td
                                className="text-xs px-3"
                                style={{
                                  maxWidth: 160,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={row.name}
                              >
                                {row.name}
                              </td>
                              <td className="text-xs px-3">R {row.rate}</td>
                              {(customerType === "company" ||
                                customerType === "individual") && (
                                <td className="text-xs px-3">
                                  {(() => {
                                    if (row.kind !== "Room") return "-";
                                    if (customerType === "company") {
                                      // Use the pre-calculated nights from the row
                                      return row.nights || "-";
                                    }
                                    // individual
                                    return (
                                      calculateNights(
                                        checkInDate,
                                        checkOutDate,
                                      ) || 0
                                    );
                                  })()}
                                </td>
                              )}

                              <td
                                className="text-xs px-3"
                                style={{ width: 140 }}
                              >
                                <div className="d-flex align-items-center">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary me-1"
                                    onClick={() =>
                                      handleQuantityChange(
                                        { childId: row.id },
                                        "decrement",
                                        row.guestIndex,
                                      )
                                    }
                                    disabled={row.quantity <= 1}
                                  >
                                    -
                                  </button>

                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-center"
                                    value={row.quantity}
                                    onChange={(e) => {
                                      const newVal =
                                        parseInt(e.target.value) || 1;
                                      if (customerType === "company") {
                                        // FIXED: Check if we have guest cards or not
                                        if (subCustomers.length > 0) {
                                          // Update only the guest this row belongs
                                          // to (by guestIndex), never every guest
                                          // that happens to share this childId.
                                          setSubCustomers((prev) =>
                                            prev.map((g, idx) =>
                                              idx === row.guestIndex
                                                ? {
                                                    ...g,
                                                    services: (
                                                      g.services || []
                                                    ).map((s) =>
                                                      Number(s.childId) ===
                                                      Number(row.id)
                                                        ? {
                                                            ...s,
                                                            quantity: newVal,
                                                          }
                                                        : s,
                                                    ),
                                                  }
                                                : g,
                                            ),
                                          );
                                        } else {
                                          // Company venue booking (no guest cards) - update selectedRooms/selectedChildren
                                          setSelectedRooms((prev) =>
                                            prev.map((room) =>
                                              Number(room.childId) ===
                                              Number(row.id)
                                                ? { ...room, quantity: newVal }
                                                : room,
                                            ),
                                          );
                                          setSelectedChildren((prev) =>
                                            prev.map((item) =>
                                              Number(item.childId) ===
                                              Number(row.id)
                                                ? { ...item, quantity: newVal }
                                                : item,
                                            ),
                                          );
                                        }
                                      } else {
                                        // Individual booking - update both rooms and children
                                        setSelectedRooms((prev) =>
                                          prev.map((room) =>
                                            Number(room.childId) ===
                                            Number(row.id)
                                              ? { ...room, quantity: newVal }
                                              : room,
                                          ),
                                        );
                                        setSelectedChildren((prev) =>
                                          prev.map((item) =>
                                            Number(item.childId) ===
                                            Number(row.id)
                                              ? { ...item, quantity: newVal }
                                              : item,
                                          ),
                                        );
                                      }
                                    }}
                                    style={{ width: 70, marginTop: -14 }}
                                  />

                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary ms-1"
                                    onClick={() =>
                                      handleQuantityChange(
                                        { childId: row.id },
                                        "increment",
                                        row.guestIndex,
                                      )
                                    }
                                  >
                                    +
                                  </button>
                                </div>
                              </td>

                              <td className="text-xs px-3">
                                R {row.total.toFixed(2)}
                              </td>
                              <td
                                className="text-xs px-2 text-end"
                                style={{ width: 40 }}
                              >
                                <button
                                  type="button"
                                  className="btn btn-link text-danger p-0"
                                  title="Remove"
                                  onClick={() => {
                                    if (customerType === "company") {
                                      // For company bookings, remove from the specific guest or global selection
                                      if (row.guestName) {
                                        // Remove from the guest this row
                                        // actually belongs to (by index) -
                                        // never match by name, since two
                                        // guests could share the same
                                        // display name.
                                        const guestIndex = row.guestIndex;
                                        if (
                                          guestIndex !== undefined &&
                                          guestIndex !== null &&
                                          guestIndex !== -1
                                        ) {
                                          setSubCustomers((prev) =>
                                            prev.map((g, idx) =>
                                              idx === guestIndex
                                                ? {
                                                    ...g,
                                                    services: (
                                                      g.services || []
                                                    ).filter(
                                                      (s) =>
                                                        Number(s.childId) !==
                                                        Number(row.id),
                                                    ),
                                                  }
                                                : g,
                                            ),
                                          );
                                        }
                                      } else {
                                        // Remove from global company selections (venue mode)
                                        setSelectedRooms((prev) =>
                                          prev.filter(
                                            (r) =>
                                              Number(r.childId) !==
                                              Number(row.id),
                                          ),
                                        );
                                        setSelectedChildren((prev) =>
                                          prev.filter(
                                            (c) =>
                                              Number(c.childId) !==
                                              Number(row.id),
                                          ),
                                        );
                                      }
                                    } else {
                                      // Individual booking - remove from both arrays
                                      setSelectedRooms((prev) =>
                                        prev.filter(
                                          (r) =>
                                            Number(r.childId) !==
                                            Number(row.id),
                                        ),
                                      );
                                      setSelectedChildren((prev) =>
                                        prev.filter(
                                          (c) =>
                                            Number(c.childId) !==
                                            Number(row.id),
                                        ),
                                      );
                                    }
                                  }}
                                >
                                  <i className="fa fa-times" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <hr />
                    <div className="row align-items-center px-4 mb-3">
                      <div className="col">
                        <p className="text-sm mb-0 fw-500">Total Amount</p>
                        <p className="text-sm text-success fw-600 mb-0">
                          R {subtotal.toFixed(2)}
                        </p>
                        {isOnAccount && (
                          <span className="badge bg-info mt-1">
                            Billing: Account
                          </span>
                        )}
                      </div>
                      <div className="col-auto d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-danger mb-0 btn-sm"
                          onClick={() => {
                            if (customerType === "company") {
                              setSubCustomers((prev) =>
                                prev.map((g) => ({ ...g, services: [] })),
                              );
                            } else {
                              setSelectedRooms([]);
                              setSelectedChildren([]);
                              setNumberOfGuests(1); // Reset guest count
                            }
                          }}
                        >
                          Clear All
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary mb-0 btn-sm"
                          disabled={!isFormValid() || loading || isSubmitting}
                          onClick={() => {
                            if (isOnAccount) {
                              // If user wants a quotation, we still need to validate existence of account owner
                              const wantsQuotation = createQuotation === true;
                              // Must have a selected existing party for credit account deduction
                              if (
                                (customerType === "individual" &&
                                  !Number(customerForm?.customerID)) ||
                                (customerType === "company" &&
                                  !Number(companyForm?.companyID))
                              ) {
                                toast.error(
                                  customerType === "company"
                                    ? "Select an existing company from suggestions to bill to account."
                                    : "Select an existing customer from suggestions to bill to account.",
                                );
                                return;
                              }

                              // Check if account is on hold before proceeding
                              const accountStatus =
                                customerType === "company"
                                  ? companyForm?.creditAccStatus
                                  : selectedCustomer?.creditAccStatus;

                              // More robust check for "on hold" status (case insensitive, trim whitespace)
                              if (
                                accountStatus &&
                                accountStatus
                                  .toString()
                                  .toLowerCase()
                                  .trim() === "on hold"
                              ) {
                                const customerName =
                                  customerType === "company"
                                    ? companyForm?.companyName || "Company"
                                    : selectedCustomer?.customerName ||
                                      "Customer";
                                setAccountHoldModal({
                                  show: true,
                                  customerType,
                                  customerName,
                                });
                                return;
                              }

                              const raw =
                                customerType === "company"
                                  ? (companyForm?.currentLimit ??
                                    companyForm?.creditLimit ??
                                    0)
                                  : (selectedCustomer?.currentLimit ??
                                    selectedCustomer?.limit ??
                                    selectedCustomer?.creditLimit ??
                                    0);
                              const available = Number(raw);
                              // If available is invalid/insufficient or exceeded, prompt user
                              if (
                                !Number.isFinite(available) ||
                                available <= 0 ||
                                subtotal > available
                              ) {
                                const diff = Number.isFinite(available)
                                  ? Math.max(subtotal - available, 0)
                                  : subtotal;
                                const reason =
                                  !Number.isFinite(available) || available <= 0
                                    ? "insufficient or negative credit limit"
                                    : `credit limit exceeded by R ${diff.toFixed(
                                        2,
                                      )}`;
                                setLimitModal({
                                  show: true,
                                  reason,
                                  diff,
                                  available: Number.isFinite(available)
                                    ? available
                                    : 0,
                                });
                                // Stash intent for modal handlers
                                setStoredPaymentData({
                                  billToAccount: true,
                                  paymentType: 4 || 10001,
                                  paidAmount: 0,
                                  discount: 0,
                                  discountType: 0,
                                  taxAmount: 0,
                                  taxPercentage: 0,
                                  utrNumber: "",
                                  notes: wantsQuotation
                                    ? `Quotation (on account) - ${reason}`
                                    : `Billed to account - ${reason}`,
                                  createQuotation: wantsQuotation,
                                  exceedLimitFlag: true,
                                });
                                return;
                              }
                              const paymentData = {
                                billToAccount: true,
                                paymentType: 4 || 10001,
                                paidAmount: 0,
                                discount: 0,
                                discountType: 0,
                                taxAmount: 0,
                                taxPercentage: 0,
                                utrNumber: "",
                                notes: "Billed to account",
                                exceedLimitFlag: false,
                              };
                              // If user explicitly wants a quotation and limit is sufficient, treat as quotation
                              if (wantsQuotation) {
                                setStoredPaymentData({
                                  ...paymentData,
                                  billToAccount: true, // quotation is still billed to account path
                                  paymentType: 4 || 10001,
                                  notes: "Quotation (on account)",
                                  createQuotation: true,
                                  exceedLimitFlag: false,
                                });
                                setEmailModal(true);
                              } else {
                                submitOrder(paymentData, 7);
                              }
                            } else {
                              setPaymentFormModal(true);
                            }
                          }}
                        >
                          {loading || isSubmitting
                            ? "Processing..."
                            : "Save and Continue"}
                        </button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Right side - Product/Service Selection */}
        <div
          className="col-lg-6 col-12"
          style={{ position: "sticky", top: "20px", alignSelf: "flex-start" }}
        >
          <div className="card mb-4">
            <div className="card-header p-4">
              <h5 className="mb-3">Select Product/ Services</h5>

              {isAccountOnHold && (
                <div className="text-muted mb-3">
                  <small>
                    Product/Service selection is disabled while account is on
                    hold.
                  </small>
                </div>
              )}
              <div className="mb-3">
                {/* Filter buttons */}
                <button
                  className={`btn btn-sm mb-0 me-1 ${
                    filterType === "All" ? "btn-primary" : "active"
                  }`}
                  type="button"
                  onClick={() => setFilterType("All")}
                  disabled={isAccountOnHold}
                >
                  All
                </button>
                <button
                  className={`btn btn-sm mb-0 me-1 ${
                    filterType === "Product" ? "btn-primary" : "active"
                  }`}
                  type="button"
                  onClick={() => setFilterType("Product")}
                  disabled={isAccountOnHold}
                >
                  Product
                </button>
                <button
                  className={`btn btn-sm mb-0 me-1 ${
                    filterType === "Service" ? "btn-primary" : "active"
                  }`}
                  type="button"
                  disabled={isAccountOnHold}
                  onClick={() => setFilterType("Service")}
                >
                  Service
                </button>
              </div>
              <div className="col-md-12">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by Product Name"
                  value={sortFilter.productSearch}
                  onChange={(e) =>
                    setSortFilter({
                      ...sortFilter,
                      productSearch: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div
              className="pos-card-wrapper-scroll-y my-custom-scrollbar-pos-card mb-3"
              style={{
                overflowY: "scroll",
                scrollbarWidth: "thin",
                maxHeight: "400px",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <div
                className="pos-card-wrapper-scroll-y my-custom-scrollbar-pos-card mb-3"
                style={{
                  overflowY: "scroll",
                  scrollbarWidth: "thin",
                  maxHeight: "400px",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                {serviceProductsApi.isError && <Error />}
                {serviceProductsApi.isLoading && <Loader height="300px" />}
                {serviceProductsApi.isSuccess && (
                  <div className="row g-2 px-4">
                    <>
                      {filteredProducts?.length > 0 ? (
                        filteredProducts.map((elem, i) => {
                          const isSelected = elem.childRecords.some((child) => {
                            if (customerType === "company") {
                              // For company bookings with guest cards - check active guest's services
                              if (activeGuestIndex !== null) {
                                const guest = subCustomers[activeGuestIndex];
                                if (guest) {
                                  return (guest.services || []).some(
                                    (s) =>
                                      s.childId?.toString() ===
                                      child.childId?.toString(),
                                  );
                                }
                              }
                              // For company venue bookings (no guest cards) OR when no active guest - check selectedRooms/selectedChildren
                              const idsOf = (obj) =>
                                [
                                  obj?.childId,
                                  obj?.serviceId,
                                  obj?.serviceID,
                                  obj?.serviceTypeId,
                                  obj?.typeId,
                                ]
                                  .filter((v) => v !== undefined && v !== null)
                                  .map((v) => v.toString());
                              const cids = idsOf(child);
                              const inChildren = selectedChildren.some((sel) =>
                                idsOf(sel).some((id) => cids.includes(id)),
                              );
                              const inRooms = selectedRooms.some((sel) =>
                                idsOf(sel).some((id) => cids.includes(id)),
                              );
                              return inChildren || inRooms;
                            }
                            // Individual booking logic
                            const idsOf = (obj) =>
                              [
                                obj?.childId,
                                obj?.serviceId,
                                obj?.serviceID,
                                obj?.serviceTypeId,
                                obj?.typeId,
                              ]
                                .filter((v) => v !== undefined && v !== null)
                                .map((v) => v.toString());
                            const cids = idsOf(child);
                            const inChildren = selectedChildren.some((sel) =>
                              idsOf(sel).some((id) => cids.includes(id)),
                            );
                            const inRooms = selectedRooms.some((sel) =>
                              idsOf(sel).some((id) => cids.includes(id)),
                            );
                            return inChildren || inRooms;
                          });

                          return (
                            <div className="col-md-3 col-sm-4 col-6" key={i}>
                              <div
                                className={`${
                                  isSelected ? "selected_item" : ""
                                } border-dashed border-1 border-secondary border-radius-md py-2 position-relative p-2 h-100 cursor-pointer`}
                                onClick={() => handleSelectProduct(elem)}
                              >
                                {elem.type === 1 ? (
                                  <span
                                    className="badge badge-primary mb-2"
                                    style={{ fontSize: "10px" }}
                                  >
                                    Product
                                  </span>
                                ) : (
                                  <span
                                    className="badge badge-dark mb-2"
                                    style={{ fontSize: "10px" }}
                                  >
                                    Service
                                  </span>
                                )}
                                {elem.iconImage && (
                                  <img
                                    src={elem.iconImage}
                                    alt=""
                                    height={40}
                                    className="mb-2 d-block"
                                  />
                                )}
                                <p className="text-xs font-weight-bold mb-0 d-block text-dark">
                                  {elem.productServiceName}
                                </p>
                                <small
                                  style={{
                                    fontSize: "12px",
                                    marginTop: "0px",
                                    display: "block",
                                  }}
                                >
                                  {elem.brandName.toLowerCase()}
                                </small>
                                <small
                                  style={{
                                    fontSize: "12px",
                                    marginTop: "0px",
                                    display: "block",
                                  }}
                                  className="text-orange fw-bold mt-1"
                                >
                                  {elem.childRecords?.length}{" "}
                                  {elem.childRecords?.length > 1
                                    ? "items"
                                    : "item"}
                                </small>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <h6 className="text-center">
                          No Products/ Services Found !!
                        </h6>
                      )}
                    </>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Payment Form Modal */}
        <Modal
          show={paymentFormModal}
          onHide={() => setPaymentFormModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Payment Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AddPaymentForm
              handleSubmit={handlePaymentSubmit}
              totalAmount={subtotal}
              setPaymentFormModal={setPaymentFormModal}
              data={paymentData}
              disabled={isAccountOnHold}
              defaultEmail={
                customerType === "individual"
                  ? customerForm?.email || selectedCustomer?.email || ""
                  : companyForm?.companyEmail || ""
              }
            />
          </Modal.Body>
        </Modal>

        {/* Credit Limit Confirmation Modal */}
        <Modal
          show={limitModal.show}
          onHide={() => setLimitModal((m) => ({ ...m, show: false }))}
          size="md"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Credit Approval Required</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-2">
              <p className="mb-1">
                The customer's account cannot cover this reservation.
              </p>
              <small className="text-muted d-block">
                Reason: {limitModal.reason}
              </small>
              {Number(limitModal.diff) > 0 && (
                <small className="text-muted d-block">
                  Shortfall: R {Number(limitModal.diff).toFixed(2)}
                </small>
              )}
            </div>
            <p className="mb-0">
              Choose Cancel to select another payment method, or Proceed to send
              this to the Credit Manager for approval.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setLimitModal((m) => ({ ...m, show: false }));
                // Switch back to normal payment path so user can choose an alternative method
                setIsOnAccount(false);
                setPaymentFormModal(true);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                const intent = getStoredPaymentData() || {};
                setLimitModal((m) => ({ ...m, show: false }));
                // If this was intended as a quotation, open email modal
                if (intent.createQuotation) {
                  setEmailModal(true);
                  return;
                }
                // Otherwise submit to Credit Manager with exceed flag
                const payload = {
                  billToAccount: true,
                  paymentType: 4 || 10001,
                  paidAmount: 0,
                  discount: 0,
                  discountType: 0,
                  taxAmount: 0,
                  taxPercentage: 0,
                  utrNumber: "",
                  notes: intent.notes || "Billed to account - credit approval",
                  exceedLimitFlag: true,
                };
                submitOrder(payload, 7);
              }}
            >
              Proceed
            </button>
          </Modal.Footer>
        </Modal>

        {/* Account on Hold Modal */}
        <Modal
          show={accountHoldModal.show}
          onHide={() => setAccountHoldModal((m) => ({ ...m, show: false }))}
          size="md"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Account on Hold</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <p className="mb-2">
                <strong>{accountHoldModal.customerName}</strong>'s account is
                currently on hold and cannot be billed to.
              </p>
              <p className="mb-2">
                Please contact the Credit Manager to resolve this issue or
                choose an alternative payment method.
              </p>
              <div className="alert alert-warning mb-0">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>Note:</strong> Account payments are temporarily
                suspended for this {accountHoldModal.customerType}.
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setAccountHoldModal((m) => ({ ...m, show: false }));
                // Switch back to normal payment path
                setIsOnAccount(false);
                setCreateQuotation(false);
                setPaymentFormModal(true);
              }}
            >
              Choose Payment Method
            </button>
          </Modal.Footer>
        </Modal>

        {/* Email Modal for Quotations */}
        <Modal
          show={emailModal}
          onHide={() => setEmailModal(false)}
          size="md"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Send Quotation</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <label className="form-label">Document Type</label>
              <select
                placeholder="Select Document Type"
                className="form-control"
                value={docSendType}
                onChange={(e) => setDocSendType(e.target.value)}
              >
                <option value="quotation">Quotation</option>
                <option value="proforma">Proforma Invoice</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Send To (Primary Email)*</label>
              <input
                type="email"
                className="form-control"
                placeholder="primary@example.com"
                value={quotationEmail}
                onChange={(e) => setQuotationEmail(e.target.value)}
                required
              />
              <small className="text-muted">Required main recipient</small>
            </div>
            <div className="mb-3">
              <label className="form-label">CC (Optional)</label>
              <input
                type="email"
                className="form-control"
                placeholder="cc@example.com"
                value={ccEmail}
                onChange={(e) => setCcEmail(e.target.value)}
              />
              <small className="text-muted">
                Optional second email (will receive a copy)
              </small>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setEmailModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={submitQuotation}
              disabled={!quotationEmail.trim()}
            >
              Send {docSendType === "proforma" ? "Proforma" : "Quotation"}
            </button>
          </Modal.Footer>
        </Modal>
        {/* Reintroduced Product Details Modal */}
        {selectedProduct && (
          <Modal
            show={isProductModalOpen}
            onHide={() => setIsProductModalOpen(false)}
            size="xl"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {selectedProduct?.productServiceName || "Product Details"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="table-responsive">
                <table className="table mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th className="text-uppercase text-secondary text-xs opacity-7">
                        Service Type
                      </th>
                      <th className="text-uppercase text-secondary text-xs opacity-7">
                        Avail Qty
                      </th>
                      <th className="text-uppercase text-secondary text-xs opacity-7">
                        Rate
                      </th>
                      <th className="text-uppercase text-secondary text-xs opacity-7">
                        Select
                      </th>
                      <th className="text-uppercase text-secondary text-xs opacity-7">
                        Qty
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedProduct?.childRecords
                      ?.filter((child) => child.child_is_active !== false)
                      ?.map((child, idx) => {
                        const isSelected = Boolean(
                          modalSelections[child.childId],
                        );
                        const selectedQty = modalSelections[child.childId] || 1;
                        const isDisabled = child.isDisabled || false; // Check if disabled due to zero quantity
                        const stockQuantity = Number(child.quantity || 0);

                        return (
                          <tr
                            key={idx}
                            className={isDisabled ? "table-secondary" : ""}
                          >
                            <td
                              className={`text-xs px-4 ${
                                isDisabled ? "text-muted" : ""
                              }`}
                              style={{
                                maxWidth: "160px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={child.typeName}
                            >
                              {child.typeName || "N/A"}
                              {isDisabled && (
                                <small className="text-danger d-block">
                                  (Out of Stock)
                                </small>
                              )}
                            </td>
                            <td
                              className={`text-xs px-4 ${
                                isDisabled ? "text-muted" : ""
                              }`}
                            >
                              <span
                                className={
                                  stockQuantity === 0
                                    ? "text-danger fw-bold"
                                    : ""
                                }
                              >
                                {stockQuantity}
                              </span>
                            </td>
                            <td
                              className={`text-xs px-4 ${
                                isDisabled ? "text-muted" : ""
                              }`}
                              style={{ width: "110px" }}
                            >
                              R {child.rate || 0}
                            </td>
                            <td className="text-xs px-4">
                              <div className="form-check m-0">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  style={{
                                    width: 20,
                                    height: 20,
                                    cursor: isDisabled
                                      ? "not-allowed"
                                      : "pointer",
                                  }}
                                  checked={isSelected}
                                  disabled={isDisabled}
                                  onChange={() => {
                                    if (isDisabled) return; // Prevent changes when disabled
                                    setModalSelections((prev) => {
                                      const next = { ...prev };
                                      if (next[child.childId])
                                        delete next[child.childId];
                                      else next[child.childId] = 1;
                                      return next;
                                    });
                                  }}
                                />
                              </div>
                            </td>
                            <td
                              className="text-xs px-4"
                              style={{ width: "130px" }}
                            >
                              {isSelected && !isDisabled && (
                                <div className="d-flex flex-column align-items-center">
                                  <input
                                    type="number"
                                    className="form-control form-control-sm text-center"
                                    value={selectedQty}
                                    onChange={(e) => {
                                      const inputValue = e.target.value;

                                      // Always update the state with whatever user types
                                      // This allows free typing of any value
                                      setModalSelections((prev) => ({
                                        ...prev,
                                        [child.childId]:
                                          inputValue === "" ? "" : inputValue,
                                      }));
                                    }}
                                    onBlur={(e) => {
                                      const inputValue = e.target.value;
                                      const numValue = parseInt(inputValue);

                                      // Only validate on blur - ensure minimum 1 and cap at stock quantity
                                      if (
                                        isNaN(numValue) ||
                                        numValue < 1 ||
                                        inputValue === ""
                                      ) {
                                        setModalSelections((prev) => ({
                                          ...prev,
                                          [child.childId]: 1,
                                        }));
                                      } else if (numValue > stockQuantity) {
                                        setModalSelections((prev) => ({
                                          ...prev,
                                          [child.childId]: stockQuantity,
                                        }));
                                      } else {
                                        // Ensure it's stored as a number
                                        setModalSelections((prev) => ({
                                          ...prev,
                                          [child.childId]: numValue,
                                        }));
                                      }
                                    }}
                                    min="1"
                                    max={stockQuantity}
                                    style={{ width: 70, marginTop: 4 }}
                                    placeholder="Qty"
                                  />
                                  <small
                                    className="text-muted mt-1"
                                    style={{ fontSize: "10px" }}
                                  >
                                    Max: {stockQuantity}
                                  </small>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setIsProductModalOpen(false)}
              >
                Close
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  if (!selectedProduct) return;
                  const children = selectedProduct.childRecords || [];
                  const childIds = new Set(
                    children.map((c) => Number(c.childId)),
                  );

                  if (customerType === "company") {
                    // If guest cards exist, require selection and assign to guest
                    if (subCustomers.length > 0) {
                      if (activeGuestIndex === null) {
                        toast.error("Select a guest card first");
                      } else {
                        setSubCustomers((prev) => {
                          const copy = [...prev];
                          const guest = { ...(copy[activeGuestIndex] || {}) };
                          const curr = Array.isArray(guest.services)
                            ? guest.services
                            : [];
                          // remove any of this product's children
                          const toAdd = children
                            .filter((c) => modalSelections[c.childId])
                            .map((c) => ({
                              childId: c.childId,
                              rate: c.rate,
                              quantity: modalSelections[c.childId] || 1,
                              typeId: c.typeId,
                              serviceTypeId: c.typeId, // Add for proper ID mapping
                              serviceId: c.serviceId || c.childId, // Add for edit mode
                              name: c.productServiceName || c.typeName || "",
                              rawName: c.typeName || c.productServiceName || "",
                              accommodation: isAccommodation(c),
                              parentId: c.parentId || 0,
                            }));
                          const stagedNameKeys = new Set(
                            toAdd.map((x) => getNameKey(x)),
                          );
                          const remaining = curr.filter(
                            (s) =>
                              !childIds.has(Number(s.childId)) &&
                              !stagedNameKeys.has(getNameKey(s)),
                          );
                          // add staged selections, de-duped by name key
                          const byName = new Map(
                            [...remaining, ...toAdd].map((x) => [
                              getNameKey(x),
                              x,
                            ]),
                          );
                          guest.services = Array.from(byName.values());

                          guest.services = dedupeGuestServices(guest.services);
                          copy[activeGuestIndex] = guest;
                          return copy;
                        });
                      }
                    } else {
                      // No guest cards - treat like individual booking (venue mode)
                      const staged = children
                        .filter((c) => modalSelections[c.childId])
                        .map((c) => ({
                          childId: c.childId,
                          typeId: c.typeId,
                          serviceTypeId: c.typeId, // Add for proper ID mapping
                          serviceId: c.serviceId || c.childId, // Add for edit mode
                          parentId: c.parentId,
                          typeName: c.typeName,
                          rate: c.rate,
                          quantity: modalSelections[c.childId] || 1,
                          maxQuantity: c.quantity,
                          accommodation: isAccommodation(c),
                        }));

                      const stagedRooms = staged.filter((s) => s.accommodation);
                      const stagedOthers = staged.filter(
                        (s) => !s.accommodation,
                      );
                      const stagedRoomNames = new Set(
                        stagedRooms.map((s) => getNameKey(s)),
                      );
                      const stagedOtherNames = new Set(
                        stagedOthers.map((s) => getNameKey(s)),
                      );

                      // Update rooms
                      setSelectedRooms((prev) => {
                        let next = prev.filter(
                          (it) =>
                            !childIds.has(Number(it.childId)) &&
                            !stagedRoomNames.has(getNameKey(it)),
                        );
                        next = next.filter(
                          (it) => !stagedOtherNames.has(getNameKey(it)),
                        );
                        const byName = new Map(
                          next.map((x) => [getNameKey(x), x]),
                        );
                        stagedRooms.forEach((s) =>
                          byName.set(getNameKey(s), s),
                        );
                        return Array.from(byName.values());
                      });

                      // Update children
                      setSelectedChildren((prev) => {
                        let next = prev.filter(
                          (it) =>
                            !childIds.has(Number(it.childId)) &&
                            !stagedOtherNames.has(getNameKey(it)),
                        );
                        next = next.filter(
                          (it) => !stagedRoomNames.has(getNameKey(it)),
                        );
                        const byName = new Map(
                          next.map((x) => [getNameKey(x), x]),
                        );
                        stagedOthers.forEach((s) =>
                          byName.set(getNameKey(s), s),
                        );
                        return Array.from(byName.values());
                      });
                    }
                  } else {
                    // Individual path: update selections for this product only
                    const staged = children
                      .filter((c) => modalSelections[c.childId])
                      .map((c) => ({
                        childId: c.childId,
                        typeId: c.typeId,
                        serviceTypeId: c.typeId, // Add for proper ID mapping
                        serviceId: c.serviceId || c.childId, // Add for edit mode
                        parentId: c.parentId,
                        typeName: c.typeName,
                        rate: c.rate,
                        quantity: modalSelections[c.childId] || 1,
                        maxQuantity: c.quantity,
                        accommodation: isAccommodation(c),
                      }));

                    const stagedRooms = staged.filter((s) => s.accommodation);
                    const stagedOthers = staged.filter((s) => !s.accommodation);
                    const stagedRoomNames = new Set(
                      stagedRooms.map((s) => getNameKey(s)),
                    );
                    const stagedOtherNames = new Set(
                      stagedOthers.map((s) => getNameKey(s)),
                    );

                    // Update rooms: remove existing from this product, then add stagedRooms
                    setSelectedRooms((prev) => {
                      let next = prev.filter(
                        (it) =>
                          !childIds.has(Number(it.childId)) &&
                          !stagedRoomNames.has(getNameKey(it)),
                      );
                      // Also exclude any rooms that collide by name with stagedOthers (safety)
                      next = next.filter(
                        (it) => !stagedOtherNames.has(getNameKey(it)),
                      );
                      // Add staged rooms deduped by name key
                      const byName = new Map(
                        next.map((x) => [getNameKey(x), x]),
                      );
                      stagedRooms.forEach((s) => byName.set(getNameKey(s), s));
                      return Array.from(byName.values());
                    });

                    // Update children: remove existing from this product, then add stagedOthers
                    setSelectedChildren((prev) => {
                      let next = prev.filter(
                        (it) =>
                          !childIds.has(Number(it.childId)) &&
                          !stagedOtherNames.has(getNameKey(it)),
                      );
                      // Also exclude any children that collide by name with stagedRooms (safety)
                      next = next.filter(
                        (it) => !stagedRoomNames.has(getNameKey(it)),
                      );
                      // Add staged others deduped by name key
                      const byName = new Map(
                        next.map((x) => [getNameKey(x), x]),
                      );
                      stagedOthers.forEach((s) => byName.set(getNameKey(s), s));
                      return Array.from(byName.values());
                    });
                  }

                  setIsProductModalOpen(false);
                  setModalSelections({});
                }}
              >
                Confirm
              </button>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default AddEditBooking;
